import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Calendar, CheckCircle2, Clock, Package, Upload, User, Play, Pause, CheckCircle, Truck, Printer, Edit, CreditCard, Users, FileText, Download, Loader2, RefreshCw, Eye, Trash2, Plus, MessageCircle, Zap, Share2, AlertTriangle, Settings } from "lucide-react";
import { DialogEmitirNota } from "@/components/DialogEmitirNota";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getOrderByCode, updateOrderStatus, type OrderRow, debugListOrdersWithPersonalizations, getStatusNotes, saveStatusNotes } from "@/integrations/supabase/orders";
import { getReceitaByOrderCode } from "@/integrations/supabase/receitas";
import "@/integrations/supabase/cleanupDuplicates"; // 🧹 Disponibilizar cleanup no console
import "./Dashboard.css"; // Importar animações
import { useSync } from "@/contexts/SyncContext";
import { getMedidas } from "@/integrations/supabase/medidas";
import { useAuth } from "@/components/AuthProvider";
import { toast } from "sonner";
import { DEFAULT_ORDER_STATUS_DETAILS, ORDER_STATUS } from "@/utils/statusConstants";
import { getCurrentEmpresaId } from "@/integrations/supabase/auth-utils";
import { useOrderStatusConfig, type OrderStatusDetail } from "@/hooks/useOrderStatusConfig";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { listarFormasPagamento, type FormaPagamento } from "@/integrations/supabase/formas-pagamento";
import { DialogRegistrarRecebimento } from "@/components/DialogRegistrarRecebimento";
import { ListaRecebimentos } from "@/components/ListaRecebimentos";
import ArquivosSection from "@/components/ArquivosSection";
import { getArquivos, ArquivoData } from "@/integrations/supabase/arquivos";
import { ProvaUnificadoModal } from "@/components/ProvaUnificadoModal";
import { updateProvaData, getProvaData } from "@/integrations/supabase/orders";
import { adicionarProvaAoCliente } from "@/integrations/supabase/prova-agendamentos";
import { notificarProvaAgendada } from "@/utils/notificacoes-prova";
import { ProvaAgendada } from "@/constants/prova-agendamento";
import { ConsertoPedidoCard } from "@/components/ConsertoPedidoCard";
import { ProvaPedidoCard } from "@/components/ProvaPedidoCard";
import { TimelineModeSwitcher } from "@/components/TimelineModeSwitcher";
import { getProductionTimeline, removeDuplicateSteps, setCurrentProductionStep, completeProductionStep, updateProductionTimeline, type ProductionTimeline, getTimelineConfig, isDefaultStep } from "@/integrations/supabase/production-timeline";
import type { TimelineMode } from "@/constants/timeline-modes";
import { getDefaultTimelineMode, setDefaultTimelineMode } from "@/integrations/supabase/timeline-config";

type FocusNFServiceType = typeof import("@/integrations/focusnf/service")["focusNFService"];

// Converter camelCase para formato legível: tipoAbertura → Tipo Abertura
function formatFieldName(fieldName: string): string {
  return fieldName
    .replace(/([A-Z])/g, ' $1') // Adiciona espaço antes de cada maiúscula
    .replace(/^./, str => str.toUpperCase()) // Primeira letra maiúscula
    .trim();
}

type OrderItem = {
  id: string;
  client: string;
  type: string;
  description: string;
  value: number;
  paid: number;
  delivery: string; // ISO
  status: "Aguardando aprovação" | "Em produção" | "Finalizando" | "Pronto" | "Aguardando retirada" | "Entregue" | "Cancelado";
  file?: string;
  personalizations?: OrderRow["personalizations"];
};


function getStatusStepIndex(status: OrderItem["status"]) {
  const steps: OrderItem["status"][] = [
    "Aguardando aprovação",
    "Em produção",
    "Finalizando",
    "Pronto",
    "Aguardando retirada",
    "Entregue",
  ];
  return steps.indexOf(status);
}

export default function PedidoDetalhe() {
  // Buscar parâmetros da URL antes de qualquer uso
  const { id } = useParams();

  // Buscar pedido (orderDb) usando o id da URL
  const { data: orderDb, isLoading, error, refetch } = useQuery({
    queryKey: ["order", id],
    queryFn: () => getOrderByCode(id),
    enabled: Boolean(id),
    refetchOnWindowFocus: false,
    staleTime: 0, // Sempre buscar dados frescos
  });

  // Buscar telefone atualizado do cliente por ID ou nome
  const [telefoneCliente, setTelefoneCliente] = useState<string | undefined>(undefined);
  useEffect(() => {
    async function buscarTelefoneCliente() {
      if (!orderDb) return;
      const { supabase } = await import('@/integrations/supabase/client');
      const { getCurrentEmpresaId } = await import('@/integrations/supabase/auth-utils');
      const empresa_id = await getCurrentEmpresaId();
      let telefone = orderDb.customer_phone || undefined;
      if (empresa_id) {
        // Preferir buscar por ID
        if ((orderDb as any).customer_id) {
          const { data: cliente } = await supabase
            .from('atelie_customers')
            .select('phone')
            .eq('id', (orderDb as any).customer_id)
            .eq('empresa_id', empresa_id)
            .maybeSingle();
          if (cliente && cliente.phone) telefone = cliente.phone;
        } else if (orderDb.customer_name) {
          // Buscar por nome se não tiver ID
          const { data: clientes } = await supabase
            .from('atelie_customers')
            .select('phone')
            .eq('empresa_id', empresa_id)
            .ilike('name', orderDb.customer_name)
            .limit(1);
          if (clientes && clientes.length > 0 && clientes[0].phone) telefone = clientes[0].phone;
        }
      }
      setTelefoneCliente(telefone);
    }
    buscarTelefoneCliente();
  }, [orderDb]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { invalidateRelated } = useSync();
  const { empresa } = useAuth();
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isPaidDialogOpen, setIsPaidDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [newPaidValue, setNewPaidValue] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [forceUpdate, setForceUpdate] = useState(0); // Para forçar re-render
  const [localStatus, setLocalStatus] = useState<string | null>(null); // Estado local do status
  const [notasFiscais, setNotasFiscais] = useState<any[]>([]);
  const [emitindoNota, setEmitindoNota] = useState(false);
  const [consultandoNota, setConsultandoNota] = useState<string | null>(null);
  const [limpandoNotasErro, setLimpandoNotasErro] = useState(false);
  const [dialogEmitirNotaOpen, setDialogEmitirNotaOpen] = useState(false);
  const [focusConfig, setFocusConfig] = useState<any | null>(null);
  const [isReemitFlow, setIsReemitFlow] = useState(false);
  const [clienteInicial, setClienteInicial] = useState<any>(null);
  const [isCustomizeDialogOpen, setIsCustomizeDialogOpen] = useState(false);
  const [statusDraft, setStatusDraft] = useState<OrderStatusDetail[]>([]);

  // Estados para forma de pagamento
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [formasPagamento, setFormasPagamento] = useState<FormaPagamento[]>([]);
  const [isPaymentMethodDialogOpen, setIsPaymentMethodDialogOpen] = useState(false);

  // Estados para recebimento parcial
  const [isRegistrarRecebimentoOpen, setIsRegistrarRecebimentoOpen] = useState(false);
  const [refreshRecebimentos, setRefreshRecebimentos] = useState(0);

  const [productionTimeline, setProductionTimeline] = useState<ProductionTimeline | null>(null);
  const [isLoadingTimeline, setIsLoadingTimeline] = useState(false);
  const [editingObsKey, setEditingObsKey] = useState<string | null>(null);
  const [editingObsValue, setEditingObsValue] = useState("");
  const [isSavingObs, setIsSavingObs] = useState(false);
  const [newStepLabel, setNewStepLabel] = useState("");
  const [newStepDescription, setNewStepDescription] = useState("");
  const [newStepIcon, setNewStepIcon] = useState("CheckCircle");
  const [newStepColor, setNewStepColor] = useState("blue");
  const [isSavingNewStep, setIsSavingNewStep] = useState(false);
  const [statusNotesMap, setStatusNotesMap] = useState<{ [key: string]: string }>({});
  const [expandedObsKey, setExpandedObsKey] = useState<string | null>(null);
  const [draggedStatusKey, setDraggedStatusKey] = useState<string | null>(null);
  const [editingStepKey, setEditingStepKey] = useState<string | null>(null);
  const [editingStepLabel, setEditingStepLabel] = useState("");
  const [editingStepDescription, setEditingStepDescription] = useState("");
  const [editingStepIcon, setEditingStepIcon] = useState("CheckCircle");
  const [editingStepColor, setEditingStepColor] = useState("blue");
  const [isSavingEditStep, setIsSavingEditStep] = useState(false);

  // Estados para customização do recibo de retirada
  const [isReceiptCustomizeOpen, setIsReceiptCustomizeOpen] = useState(false);

  // Estados para agendamento de prova (FASE 3)
  const [isProvaModalOpen, setIsProvaModalOpen] = useState(false);
  const [provaAgendada, setProvaAgendada] = useState<ProvaAgendada | null>(null);

  const [selectedMode, setSelectedMode] = useState<TimelineMode>("confeccao");
  const [isDefaultModeCustom, setIsDefaultModeCustom] = useState(false); // Se modo padrão da empresa é "custom"
  const [showCustomModeInfo, setShowCustomModeInfo] = useState(false); // Mostrar card informativo
  const [showCustomizationRequiredDialog, setShowCustomizationRequiredDialog] = useState(false); // Dialog quando custom não foi customizado
  const defaultModeLoadedRef = useRef(false); // Rastrear se modo foi carregado do banco

  // Estados para modal de recebimento ao marcar como ENTREGUE
  const [isDeliveryPaymentOpen, setIsDeliveryPaymentOpen] = useState(false);
  const [deliveryPaymentAmount, setDeliveryPaymentAmount] = useState<number | null>(null);
  const [isRegisteringDeliveryPayment, setIsRegisteringDeliveryPayment] = useState(false);
  const [receiptOptions, setReceiptOptions] = useState({
    includeItems: true,
    includePersonalizations: true,
    includeDescription: false,
    isPartialDelivery: false,
    selectedItemsForDelivery: {} as { [key: string]: number },
    deliveryDate: new Date().toISOString().split('T')[0],
    includeDeliveryDate: false,
  });

  // Estados para seleção de via do cupom
  const [isSelectingCupomVia, setIsSelectingCupomVia] = useState(false);
  const [cupomStep, setCupomStep] = useState<'via' | 'customizacao'>('via');
  const [selectedCupomVia, setSelectedCupomVia] = useState<'cliente' | 'estabelecimento' | 'ambas' | null>(null);
  const [cupomPrinterType, setCupomPrinterType] = useState<'thermica' | 'a4'>('thermica');
  const [cupomOptions, setCupomOptions] = useState({
    mostrarAvisoBoas: true,
    mostrarAvisoPromocao: true,
    observacaoCustomizada: '',
  });

  // Estados para arquivos
  const [arquivos, setArquivos] = useState<ArquivoData[]>([]);
  const [isLoadingArquivos, setIsLoadingArquivos] = useState(false);

  const isHomologacaoSemTokenMemo = useMemo(() => {
    const tokenHomologacao = focusConfig?.token_homologacao?.trim();
    return focusConfig?.ambiente === "homologacao" && !tokenHomologacao;
  }, [focusConfig?.ambiente, focusConfig?.token_homologacao]);

  const {
    statusDetails,
    statusOptions,
    saveStatusConfigs,
    resetStatusConfigs,
    isSaving: isSavingStatusConfigs,
    isResetting: isResettingStatusConfigs,
  } = useOrderStatusConfig();

  const statusDetailsMap = useMemo(
    () => new Map(statusDetails.map((detail) => [detail.key, detail])),
    [statusDetails]
  );

  // Combinar statusOptions (padrão) com etapas customizadas
  const timelineStepOptions = useMemo(() => {
    if (!productionTimeline?.steps || productionTimeline.steps.length === 0) {
      return [];
    }

    const options = productionTimeline.steps.map(step => ({
      value: step.key,
      label: step.label,
      key: step.key,
    }));

    return options;
  }, [productionTimeline?.steps]);

  useEffect(() => {
    if (!orderDb?.id) return;
    const loadTimeline = async () => {
      try {
        setIsLoadingTimeline(true);

        // Primeiro, remover duplicatas se existirem
        await removeDuplicateSteps(orderDb.id);

        const [timeline, notes] = await Promise.all([
          getProductionTimeline(orderDb.id),
          getStatusNotes(orderDb.id),
        ]);
        setProductionTimeline(timeline);
        setStatusNotesMap(notes);
      } catch (error) {
      } finally {
        setIsLoadingTimeline(false);
      }
    };
    loadTimeline();
  }, [orderDb?.id]);

  useEffect(() => {
    if (!empresa?.id) return;
    const loadDefaultMode = async () => {
      const mode = await getDefaultTimelineMode(empresa.id);
      setSelectedMode(mode);
      setIsDefaultModeCustom(mode === "custom"); // Marcar se o padrão é custom
      defaultModeLoadedRef.current = true; // Marcar que foi carregado do banco
    };
    loadDefaultMode();
  }, [empresa?.id]);

  // Callback quando custom mode é selecionado
  const handleCustomModeSelected = () => {
    if (!isDefaultModeCustom) {
      // Mostrar card informativo por 2 minutos
      setShowCustomModeInfo(true);
      setTimeout(() => setShowCustomModeInfo(false), 120000);
    }
  };

  // Resetar newStatus quando o modo de timeline muda
  useEffect(() => {
    setNewStatus("");
  }, [selectedMode]);


  // Sempre aplicar o modo correto quando muda, sem condições de productionTimeline
  useEffect(() => {
    if (!selectedMode || !empresa?.id) return;
    if (!orderDb?.id) return;

    const applyAndSaveMode = async () => {
      // Se productionTimeline ainda não foi carregado, esperar
      if (!productionTimeline) return;
      let steps: any[];

      if (selectedMode === "custom") {
        const config = await getTimelineConfig();
        const hasCustomSteps = config.default_steps.some(s => s.key.startsWith('CUSTOM_'));
        const hasEdits = config.default_steps.some(s => {
          const defaultLabels: Record<string, string> = {
            "TECIDO": "Tecido",
            "CORTE": "Corte",
            "ESTAMPA": "Estampa",
            "COSTURA": "Costura",
            "OFICINA": "Oficina",
            "PRONTO": "Pronto",
            "AGUARDANDO_RETIRADA": "Aguardando Retirada",
            "ENTREGUE": "Entregue"
          };
          return s.label !== (defaultLabels[s.key] || s.label);
        });
        const hasCustomCount = config.default_steps.length !== 8;
        const hasCustomizations = hasCustomSteps || hasEdits || hasCustomCount;

        if (!hasCustomizations) {
          setShowCustomizationRequiredDialog(true);
          return;
        }

        steps = config.default_steps.map(s => ({
          key: s.key,
          label: s.label,
          description: s.description,
          completed: false,
          notes: "",
          color: s.color || "blue",
          icon: s.icon || "CheckCircle"
        }));
      } else {
        const stepsPresets: Record<string, any[]> = {
          atelie: [
            { key: "RECEPÇÃO", label: "Recepção", description: "", completed: false, notes: "", color: "blue", icon: "Package" },
            { key: "DIAGNÓSTICO", label: "Diagnóstico", description: "", completed: false, notes: "", color: "purple", icon: "Search" },
            { key: "ORÇAMENTO", label: "Orçamento", description: "", completed: false, notes: "", color: "yellow", icon: "FileText" },
            { key: "EXECUÇÃO", label: "Execução", description: "", completed: false, notes: "", color: "orange", icon: "Hammer" },
            { key: "PROVA", label: "Prova", description: "", completed: false, notes: "", color: "green", icon: "CheckCircle2" },
            { key: "AJUSTES", label: "Ajustes", description: "", completed: false, notes: "", color: "pink", icon: "Edit" },
            { key: "PRONTO", label: "Pronto", description: "", completed: false, notes: "", color: "emerald", icon: "CheckCircle" },
            { key: "ENTREGUE", label: "Entregue", description: "", completed: false, notes: "", color: "gray", icon: "Truck" }
          ],
          confeccao: [
            { key: "TECIDO", label: "Tecido", description: "", completed: false, notes: "", color: "blue", icon: "Layers" },
            { key: "CORTE", label: "Corte", description: "", completed: false, notes: "", color: "cyan", icon: "Scissors" },
            { key: "ESTAMPA", label: "Estampa", description: "", completed: false, notes: "", color: "indigo", icon: "Palette" },
            { key: "COSTURA", label: "Costura", description: "", completed: false, notes: "", color: "violet", icon: "Needle" },
            { key: "OFICINA", label: "Oficina", description: "", completed: false, notes: "", color: "orange", icon: "Hammer" },
            { key: "PRONTO", label: "Pronto", description: "", completed: false, notes: "", color: "emerald", icon: "CheckCircle" },
            { key: "AGUARDANDO_RETIRADA", label: "Aguardando Retirada", description: "", completed: false, notes: "", color: "yellow", icon: "Clock" },
            { key: "ENTREGUE", label: "Entregue", description: "", completed: false, notes: "", color: "gray", icon: "Truck" }
          ],
          grafica: [
            { key: "ARQUIVO", label: "Arquivo", description: "", completed: false, notes: "", color: "blue", icon: "FileJson" },
            { key: "APROVACAO", label: "Aprovação", description: "", completed: false, notes: "", color: "yellow", icon: "CheckCircle2" },
            { key: "MONTAGEM", label: "Montagem", description: "", completed: false, notes: "", color: "purple", icon: "Package" },
            { key: "PRODUCAO", label: "Produção", description: "", completed: false, notes: "", color: "orange", icon: "Zap" },
            { key: "ACABAMENTO", label: "Acabamento", description: "", completed: false, notes: "", color: "cyan", icon: "Wand2" },
            { key: "QUALIDADE", label: "Qualidade", description: "", completed: false, notes: "", color: "green", icon: "CheckCircle" },
            { key: "PRONTO", label: "Pronto", description: "", completed: false, notes: "", color: "emerald", icon: "CheckCircle" },
            { key: "ENTREGUE", label: "Entregue", description: "", completed: false, notes: "", color: "gray", icon: "Truck" }
          ]
        };
        steps = stepsPresets[selectedMode] || stepsPresets.confeccao;
      }

      if (steps && steps.length > 0) {
        let updatedSteps = steps.map(s => ({ ...s }));

        if (selectedMode !== "custom") {
          const customSteps = productionTimeline.steps.filter(s => s.key?.startsWith('CUSTOM_'));
          updatedSteps = [...updatedSteps, ...customSteps];
        }

        setProductionTimeline({ ...productionTimeline, steps: updatedSteps });

        // Salvar as etapas no banco para sincronizar com o novo modo
        await updateProductionTimeline(orderDb!.id, updatedSteps, productionTimeline.current_step_key);
        await setDefaultTimelineMode(empresa.id, selectedMode);
      }
    };

    applyAndSaveMode();
  }, [selectedMode, empresa?.id]);

  // Carregar dados de prova agendada (FASE 3)
  useEffect(() => {
    if (!orderDb?.id) return;
    const loadProvaData = async () => {
      try {
        const data = await getProvaData(orderDb.id);
        if (data) {
          setProvaAgendada(data);
        }
      } catch (error) {
        console.error("Erro ao carregar dados de prova:", error);
      }
    };
    loadProvaData();
  }, [orderDb?.id]);

  // Exibir avisos de estoque (se existirem) após redirecionamento do "Novo Pedido".
  useEffect(() => {
    if (!id) return;
    const key = `inventoryWarnings:${id}`;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return;
      localStorage.removeItem(key);

      const warnings = JSON.parse(raw) as Array<{ level?: string; item_name?: string; message?: string }>;
      if (!Array.isArray(warnings) || warnings.length === 0) return;

      const negative = warnings.filter((w) => w.level === "negative").length;
      const low = warnings.filter((w) => w.level === "low").length;
      const preview = warnings
        .slice(0, 3)
        .map((w) => `- ${w.item_name ?? "Item"}: ${w.message ?? "Atenção no estoque"}`)
        .join("\n");

      toast.warning(
        `Atenção no estoque: ${negative} item(ns) ficaram negativos e ${low} item(ns) ficaram abaixo do mínimo.`,
        {
          duration: 8000,
          description: preview,
        }
      );
    } catch {
      // Ignorar parsing/storage errors
    }
  }, [id]);

  // Carregar arquivos quando o pedido é carregado
  useEffect(() => {
    if (!orderDb?.id) return;

    const loadArquivos = async () => {
      setIsLoadingArquivos(true);
      try {
        const arquivosCarregados = await getArquivos('orders', orderDb.id);
        setArquivos(arquivosCarregados);
      } catch (error) {
        console.error("Erro ao carregar arquivos:", error);
      } finally {
        setIsLoadingArquivos(false);
      }
    };

    loadArquivos();
  }, [orderDb?.id]);

  const handleOpenCustomizeStatuses = useCallback(() => {
    setStatusDraft(statusDetails.map((detail) => ({ ...detail })));
    setIsCustomizeDialogOpen(true);
  }, [statusDetails]);

  const handleStatusDraftChange = useCallback(
    (key: OrderStatusDetail["key"], field: "label" | "description", value: string) => {
      setStatusDraft((previous) =>
        previous.map((detail) => {
          if (detail.key !== key) {
            return detail;
          }

          if (field === "label") {
            return { ...detail, label: value };
          }

          return { ...detail, description: value };
        })
      );
    },
    []
  );

  const handleStatusDraftRestore = useCallback((key: OrderStatusDetail["key"]) => {
    setStatusDraft((previous) =>
      previous.map((detail) =>
        detail.key === key
          ? {
              ...detail,
              label: detail.defaultLabel,
              description: detail.defaultDescription,
            }
          : detail
      )
    );
  }, []);

  const handleSaveStatusDraft = useCallback(async () => {
    const hasEmpty = statusDraft.some((detail) => detail.label.trim().length === 0);

    if (hasEmpty) {
      toast.error("O nome das etapas não pode ficar vazio.");
      return;
    }

    try {
      const payload = statusDraft.map((detail) => {
        const description = (detail.description ?? "").trim();
        return {
          status_key: detail.key,
          label: detail.label.trim(),
          description: description.length > 0 ? description : null,
        };
      });

      const result = await saveStatusConfigs(payload);

      if (!result.ok) {
        toast.error(result.error || "Erro ao salvar etapas personalizadas.");
        return;
      }

      // Sincronizar edições de statusDraft com productionTimeline
      if (productionTimeline && orderDb?.id) {
        const syncedSteps = productionTimeline.steps.map((step) => {
          const draftStatus = statusDraft.find((s) => s.key === step.key);
          if (draftStatus && draftStatus.label !== step.label) {
            console.log(`🔄 [SINCRONIZANDO] ${step.key}: "${step.label}" → "${draftStatus.label}"`);
            return { ...step, label: draftStatus.label, description: draftStatus.description };
          }
          return step;
        });

        const timelineResult = await updateProductionTimeline(orderDb.id, syncedSteps);
        if (!timelineResult.ok) {
          toast.error("Erro ao salvar timeline");
          return;
        }
      }

      toast.success("Etapas atualizadas com sucesso!");
      setIsCustomizeDialogOpen(false);
    } catch (error) {
      console.error("Erro ao salvar etapas personalizadas:", error);
      toast.error("Erro ao salvar etapas personalizadas.");
    }
  }, [saveStatusConfigs, statusDraft, productionTimeline, orderDb?.id]);

  const handleResetStatuses = useCallback(async () => {
    try {
      const result = await resetStatusConfigs();

      if (!result.ok) {
        toast.error(result.error || "Erro ao restaurar etapas padrão.");
        return;
      }

      const defaultDraft: OrderStatusDetail[] = DEFAULT_ORDER_STATUS_DETAILS.map((detail) => ({
        key: detail.key as OrderStatusDetail["key"],
        label: detail.label,
        description: detail.description,
        defaultLabel: detail.label,
        defaultDescription: detail.description,
      }));

      setStatusDraft(defaultDraft);
      toast.success("Etapas restauradas para o padrão!");
    } catch (error) {
      console.error("Erro ao restaurar etapas padrão:", error);
      toast.error("Erro ao restaurar etapas padrão.");
    }
  }, [resetStatusConfigs]);

  const code = id as string;
  const focusNFServiceRef = useRef<FocusNFServiceType | null>(null);

  // 🔍 DEBUG: Listar pedidos com personalizations ao abrir a página
  useEffect(() => {
    const debug = async () => {
      console.log("[DEBUG PAGE] Carregando lista de pedidos com personalizations...");
      const ordersWithPersonalizations = await debugListOrdersWithPersonalizations();
      console.log("[DEBUG PAGE] Pedidos com personalizations:", ordersWithPersonalizations);
    };
    debug();
  }, []);

  const getFocusNFService = useCallback(async () => {
    if (!focusNFServiceRef.current) {
      const module = await import("@/integrations/focusnf/service");
      focusNFServiceRef.current = module.focusNFService;
    }
    return focusNFServiceRef.current;
  }, []);

  // Carregar notas fiscais do pedido
  const loadNotasFiscais = useCallback(async () => {
    try {
      if (!empresa?.tem_nota_fiscal) {
        setNotasFiscais([]);
        return;
      }

      const focusNF = await getFocusNFService();
      if (!focusNF) {
        setNotasFiscais([]);
        return;
      }

      const notas = await focusNF.listarNotas(code);
      setNotasFiscais(notas);
    } catch (error) {
      console.error('[PedidoDetalhe] Erro ao carregar notas fiscais:', error);
      setNotasFiscais([]);
    }
  }, [code, empresa?.tem_nota_fiscal, getFocusNFService]);

  useEffect(() => {
    const loadConfig = async () => {
      const focusNF = await getFocusNFService();
      if (!focusNF) return;
      const config = await focusNF.getConfig();
      setFocusConfig(config);
    };

    loadConfig();
  }, [getFocusNFService]);


  const handleConsultarNota = useCallback(async (ref: string, showLoading = true) => {
    try {
      if (isHomologacaoSemTokenMemo) {
        toast.error('Token de homologação não configurado. Cadastre o emitente e preencha o token de homologação.');
        return;
      }

      if (showLoading) {
        setConsultandoNota(ref);
      }

      const empresa_id = await getCurrentEmpresaId();
      if (!empresa_id) {
        toast.error('Empresa não encontrada');
        return;
      }

      const focusNF = await getFocusNFService();
      if (!focusNF) {
        toast.error('Serviço de notas fiscais indisponível');
        return;
      }

      // Buscar o tipo_nota da nota armazenada
      const { supabase } = await import('@/integrations/supabase/client');
      const { data: notaExistente } = await supabase
        .from('focusnf_notas')
        .select('tipo_nota')
        .eq('ref', ref)
        .eq('empresa_id', empresa_id)
        .single();

      const tipoNota = notaExistente?.tipo_nota || 'NFe';
      console.log('[FocusNF] Consultando nota com tipoNota:', tipoNota);

      const result = await focusNF.consultarNota(ref, false, tipoNota);
      
      if (result.ok && result.data) {
        // Atualizar nota no banco de dados
        const notaData = result.data;

        // Buscar a nota atual no banco
        const { data: notaExistente } = await supabase
          .from('focusnf_notas')
          .select('id')
          .eq('ref', ref)
          .eq('empresa_id', empresa_id)
          .single();

        if (notaExistente) {
          // Atualizar status e dados da nota
          await supabase
            .from('focusnf_notas')
            .update({
              status: (notaData as any).status || 'processando_autorizacao',
              numero: (notaData as any).numero,
              serie: (notaData as any).serie,
              chave_acesso: (notaData as any).chave_nfe || (notaData as any).chave_acesso,
              xml_url: (notaData as any).caminho_xml_nota_fiscal,
              danfe_url: (notaData as any).caminho_danfe,
              dados_retornados: notaData,
              // Só salvar como erro se realmente for um erro (não autorizado)
              erro_mensagem: (notaData as any).status === 'autorizado' || (notaData as any).status === 'processando_autorizacao'
                ? null // Limpar erro se autorizado
                : ((notaData as any).erro_mensagem || (notaData as any).mensagem_sefaz || null),
              updated_at: new Date().toISOString(),
            })
            .eq('id', notaExistente.id);
        }

        // Recarregar lista de notas diretamente
        const notasAtualizadas = await focusNF.listarNotas(code);
        setNotasFiscais(notasAtualizadas);

        if (showLoading) {
          if (notaData.status === 'autorizado') {
            toast.success('Nota fiscal autorizada!');
          } else if (notaData.status === 'erro_autorizacao' || notaData.status === 'denegado') {
          } else {
            toast.info('Status ainda em processamento');
          }
        }
      } else {
        if (showLoading) {
          toast.error(result.error || 'Erro ao consultar nota');
        }
      }
    } catch (error: any) {
      console.error('Erro ao consultar nota:', error);
      if (showLoading) {
        toast.error(error.message || 'Erro ao consultar nota');
      }
    } finally {
      if (showLoading) {
        setConsultandoNota(null);
      }
    }
  }, [code, getFocusNFService, isHomologacaoSemTokenMemo]);

  // Carregar notas fiscais quando o código mudar
  useEffect(() => {
    if (code) {
      loadNotasFiscais();
    }
  }, [code, loadNotasFiscais]);

  // Polling automático para notas em processamento
  useEffect(() => {
    const notasProcessando = notasFiscais.filter(
      nota => nota.status === 'processando_autorizacao'
    );

    if (notasProcessando.length === 0) {
      return; // Não há notas para consultar
    }

    // Consultar a cada 10 segundos
    const interval = setInterval(() => {
      notasProcessando.forEach((nota) => {
        handleConsultarNota(nota.ref, false); // false = não mostrar loading
      });
    }, 10000); // 10 segundos

    return () => clearInterval(interval);
  }, [notasFiscais, handleConsultarNota]);

  const handleEmitirNota = async () => {
    if (!order) {
      toast.error('Pedido não encontrado');
      return;
    }

    if (!empresa?.tem_nota_fiscal) {
      toast.info('Funcionalidade disponível no plano Profissional (com NF). Atualize seu plano para emitir notas fiscais.');
      navigate('/assinatura');
      return;
    }

    if (isHomologacaoSemTokenMemo) {
      toast.error('Token de homologação não configurado. Cadastre o emitente e preencha o token de homologação.');
      return;
    }

    const hasAutorizada = notasFiscais.some(
      (nota) => nota.status === 'autorizado' || nota.status === 'autorizada'
    );
    if (hasAutorizada || isReemitFlow) {
      const confirmar = window.confirm(
        'Já existe uma nota autorizada para este pedido. Reemitir vai gerar uma nova referência e nova numeração. Deseja continuar?'
      );
      if (!confirmar) {
        setIsReemitFlow(false);
        return;
      }
    }

    // Buscar dados do cliente para preencher o dialog
    let clienteData: any = {
      nome: order.client,
      email: orderDb?.customer_email || undefined,
      telefone: orderDb?.customer_phone || undefined,
    };

    // Tentar buscar cliente completo na tabela atelie_customers
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const { getCurrentEmpresaId } = await import('@/integrations/supabase/auth-utils');
      const empresa_id = await getCurrentEmpresaId();
      
      if (empresa_id && order.client) {
        const { data: clientes } = await supabase
          .from('atelie_customers')
          .select('*')
          .eq('empresa_id', empresa_id)
          .ilike('name', order.client)
          .limit(1);

        // Pegar o primeiro resultado (pode haver múltiplos com mesmo nome)
        const cliente = clientes && clientes.length > 0 ? clientes[0] : null;

        if (cliente) {
          clienteData = {
            nome: cliente.name || clienteData.nome,
            email: cliente.email || clienteData.email,
            telefone: cliente.phone || clienteData.telefone,
            cpf_cnpj: cliente.cpf_cnpj || undefined,
            endereco: {
              logradouro: cliente.endereco_logradouro || undefined,
              numero: cliente.endereco_numero || undefined,
              complemento: cliente.endereco_complemento || undefined,
              bairro: cliente.endereco_bairro || undefined,
              cidade: cliente.endereco_cidade || undefined,
              uf: cliente.endereco_uf || undefined,
              cep: cliente.endereco_cep || undefined,
            }
          };
        }
      }
    } catch (error) {
      console.log('Não foi possível buscar dados completos do cliente:', error);
      // Continuar com dados básicos do pedido
    }

    // Armazenar dados do cliente e abrir dialog
    setClienteInicial(clienteData);
    setDialogEmitirNotaOpen(true);
  };

  const handleConfirmarEmitirNota = async (
    items: any[],
    tipoNota: 'NFe' | 'NFCe' | 'NFSe',
    clienteData: any,
    nfse?: {
      prestadorCodigoMunicipio: string;
      prestadorInscricaoMunicipal?: string;
      tomadorCodigoMunicipio: string;
      codigoMunicipioPrestacao?: string;
      itemListaServico: string;
      codigoTributarioMunicipio?: string;
      aliquota: number;
      issRetido: boolean;
      discriminacao: string;
      naturezaOperacao?: number;
      regimeEspecialTributacao?: number;
      optanteSimplesNacional?: boolean;
      incentivadorCultural?: boolean;
      baseCalculoPisCofins?: number | string;
      aliquotaPis?: number | string;
      aliquotaCofins?: number | string;
      valorPis?: number | string;
      valorCofins?: number | string;
      tipoRetencaoPisCofins?: number | string;
      valorCsll?: number | string;
      valorIrrf?: number | string;
    }
  ) => {
    if (!order) {
      toast.error('Pedido não encontrado');
      return;
    }

    try {
      setEmitindoNota(true);
      const valorTotal = items.reduce((acc, item) => acc + item.valor_total, 0);

      const focusNF = await getFocusNFService();
      if (!focusNF) {
        toast.error('Serviço de notas fiscais indisponível');
        setEmitindoNota(false);
        return;
      }
      
      // Usar dados do cliente passados do dialog (já coletados/completados pelo usuário)
      const payload = {
        orderCode: (order as any).code || code,
        tipoNota: tipoNota,
        cliente: clienteData,
        valorTotal: valorTotal,
        items: items, // Enviar múltiplos itens
        nfse,
      };

      console.log('[FocusNF] emitirNota payload', {
        tipoNota,
        orderCode: (order as any).code || code,
      });

      let result = isReemitFlow
        ? await focusNF.reemitirNota((order as any).code || code, payload, 'Reemissão solicitada pelo usuário')
        : await focusNF.emitirNota(payload);

      // Fallback automático: se há nota anterior com erro, reemitir usando os dados preenchidos no modal completo
      if (
        !isReemitFlow &&
        !result.ok &&
        typeof result.error === 'string' &&
        result.error.toLowerCase().includes('use a ação de reemitir')
      ) {
        console.log('[FocusNF] Fallback automático para reemissão via modal completo');
        result = await focusNF.reemitirNota(
          (order as any).code || code,
          payload,
          'Reemissão automática após nota anterior com erro'
        );
      }

      // Sempre recarregar a lista de notas, mesmo se houver erro
      await loadNotasFiscais();

      if (result.ok) {
        toast.success(isReemitFlow ? 'Nota fiscal reemitida com sucesso!' : 'Nota fiscal emitida com sucesso!');
        setDialogEmitirNotaOpen(false);
        setIsReemitFlow(false);
      } else {
        toast.error(result.error || 'Erro ao emitir nota fiscal');
      }
    } catch (error: any) {
      // Recarregar lista mesmo em caso de erro para mostrar status atualizado
      await loadNotasFiscais();
      toast.error(error.message || 'Erro ao emitir nota fiscal');
    } finally {
      setEmitindoNota(false);
    }
  };

  const handleConfirmProvaAgendamento = async (prova: ProvaAgendada) => {
    console.log("🎬 [handleConfirmProvaAgendamento] INICIANDO com prova:", prova);
    try {
      console.log("🎬 [handleConfirmProvaAgendamento] Verificando orderDb.id:", orderDb?.id, "code:", code);
      if (!orderDb?.id || !code) {
        console.error("🎬 [handleConfirmProvaAgendamento] ERRO - orderDb?.id ou code não encontrado");
        toast.error("Erro: Pedido não identificado");
        return;
      }

      console.log("🎬 [handleConfirmProvaAgendamento] Salvando prova no pedido...");
      // Salvar prova no pedido (bloqueante)
      const updateResult = await updateProvaData(orderDb.id, prova);
      console.log("🎬 [handleConfirmProvaAgendamento] Resultado updateProvaData:", updateResult);
      if (!updateResult.ok) {
        console.error("🎬 [handleConfirmProvaAgendamento] Erro ao salvar prova:", updateResult.error);
        toast.error("Erro ao salvar agendamento de prova");
        return;
      }
      console.log("🎬 [handleConfirmProvaAgendamento] Prova salva com sucesso no pedido");

      // Atualizar estado local imediatamente (não-bloqueante)
      setProvaAgendada(prova);
      toast.success("✅ Prova salva com sucesso!");

      // Operações assíncronas em background (não-bloqueantes)
      if (order?.customer_name) {
        console.log("🎬 [PROVA REGISTER] Iniciando busca de customer_id para:", order.customer_name);
        // Buscar customer_id pelo nome
        (async () => {
          const { data: customerData } = await supabase
            .from('customers')
            .select('id')
            .eq('name', order.customer_name)
            .eq('empresa_id', empresa?.id)
            .maybeSingle();

          console.log("🎬 [PROVA REGISTER] Resultado da busca:", { customer_name: order.customer_name, found: !!customerData?.id, customer_id: customerData?.id });

          if (customerData?.id) {
            console.log("🎬 [PROVA REGISTER] Chamando adicionarProvaAoCliente com:", { clienteId: customerData.id, clienteNome: order.customer_name, pedidoNumero: code });
            adicionarProvaAoCliente(customerData.id, order.customer_name, code, prova).catch(err => {
              console.error("🎬 [PROVA REGISTER] Erro ao adicionar prova:", err);
            });
          } else {
            console.warn("🎬 [PROVA REGISTER] Cliente não encontrado!");
          }
        })();
      }

      if (order?.customer_email && order?.customer_name) {
        notificarProvaAgendada(
          order.customer_email,
          order.customer_name,
          prova.data_prova,
          prova.hora_prova,
          prova.local_prova,
          code
        ).catch(() => {});
      }

      if (productionTimeline) {
        setCurrentProductionStep(orderDb.id, 'PROVA')
          .then((timelineResult) => {
            if (timelineResult.ok) {
              const updatedSteps = productionTimeline.steps.map(s =>
                s.key === 'PROVA' ? { ...s, completed: true } : s
              );
              setProductionTimeline({ ...productionTimeline, steps: updatedSteps, current_step_key: 'PROVA' });
            }
          })
          .catch(() => {});
      }

      // Recarregar dados em background
      refetch().catch(() => {});
      queryClient.invalidateQueries({ queryKey: ["order", code] }).catch(() => {});
    } catch (error) {
      console.error("Erro ao salvar prova:", error);
      toast.error("Erro ao salvar prova");
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) {
      toast.error("Selecione um status");
      return;
    }

    // Validar se o status selecionado existe nas etapas disponíveis
    const statusExists = productionTimeline?.steps.some(s => s.key === newStatus);
    if (!statusExists) {
      toast.error("Etapa selecionada não existe no modo atual. Por favor, selecione novamente.");
      setNewStatus("");
      return;
    }

    // Se estiver marcando como ENTREGUE, abrir modal de recebimento
    if (newStatus === 'ENTREGUE') {
      setDeliveryPaymentAmount(null);
      setIsDeliveryPaymentOpen(true);
      return;
    }

    // Se estiver marcando como PROVA (e for conserto/ajuste), abrir modal de prova
    if (newStatus === 'PROVA' && (orderDb?.type === 'conserto' || orderDb?.type === 'ajuste')) {
      setIsProvaModalOpen(true);
      return;
    }

    try {
      // Verificar se é uma etapa padrão ou customizada (comparar com chaves reais da timeline)
      const isPadraoStep = isDefaultStep(newStatus);

      // Atualizar current_step_key (SEMPRE, seja padrão ou customizada)
      if (orderDb?.id && productionTimeline) {
        const timelineResult = await setCurrentProductionStep(orderDb.id, newStatus);
        if (!timelineResult.ok) {
          toast.error("Erro ao atualizar etapa na timeline");
          return;
        }

        // Marcar a etapa como concluída automaticamente
        const completeResult = await completeProductionStep(orderDb.id, newStatus);
        if (!completeResult.ok) {
          console.warn("Aviso: Etapa marcada como atual, mas não foi possível marcar como concluída:", completeResult.error);
        }

        // Atualizar estado local da timeline
        const updatedSteps = productionTimeline.steps.map(s =>
          s.key === newStatus ? { ...s, completed: true } : s
        );
        setProductionTimeline({ ...productionTimeline, steps: updatedSteps, current_step_key: newStatus });
      }

      // Atualizar order.status APENAS se for etapa padrão (proteger financeiro)
      if (isPadraoStep) {
        // 🔧 Converter chave da timeline para valor ORDER_STATUS (esperado pelo banco)
        const statusMappings: Record<string, string> = {
          'TECIDO': ORDER_STATUS.AGUARDANDO_APROVACAO,
          'CORTE': ORDER_STATUS.EM_PRODUCAO,
          'ESTAMPA': ORDER_STATUS.EM_PRODUCAO,
          'COSTURA': ORDER_STATUS.EM_PRODUCAO,
          'OFICINA': ORDER_STATUS.EM_PRODUCAO,
          'PRONTO': ORDER_STATUS.PRONTO,
          'ENTREGUE': ORDER_STATUS.ENTREGUE,
          'AGUARDANDO_RETIRADA': ORDER_STATUS.AGUARDANDO_RETIRADA,
        };
        const statusToUpdate = statusMappings[newStatus] || newStatus;

        setLocalStatus(statusToUpdate);
        const result = await updateOrderStatus(code, statusToUpdate as any);
        if (!result.ok) {
          console.error("❌ Erro ao atualizar status - result:", result);
          console.error("Status enviado:", statusToUpdate, "Código:", code);
          toast.error(result.error || "Erro ao atualizar status");
          setLocalStatus(null);
          return;
        }
      } else {
        // Etapa customizada - só atualiza timeline, não order.status
        setLocalStatus(newStatus); // Feedback visual local
      }

      toast.success("Status atualizado com sucesso!");
      setIsStatusDialogOpen(false);
      setNewStatus("");

      // 🔧 Invalidar cache de pedidos para atualizar Agenda e outras páginas
      invalidateRelated('orders');
      invalidateRelated('receitas');

      await queryClient.invalidateQueries({ queryKey: ["order", code] });
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      await queryClient.invalidateQueries({ queryKey: ["receitas"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });

      refetch();

    } catch (error) {
      console.error("❌ [handleUpdateStatus] Erro:", error);
      console.error("Stack:", error instanceof Error ? error.stack : "N/A");
      toast.error("Erro ao atualizar status");

      // Reverter estado local em caso de erro
      setLocalStatus(null);
    }
  };



  const handleConfirmDeliveryPayment = async () => {
    console.log(`💳 [handleConfirmDeliveryPayment] Iniciando fluxo de recebimento...`);
    console.log(`   Pedido ID: ${orderDb?.id}`);
    console.log(`   Valor total do pedido: R$ ${order?.value.toFixed(2)}`);
    console.log(`   Valor a receber: R$ ${(deliveryPaymentAmount || 0).toFixed(2)}`);

    setIsRegisteringDeliveryPayment(true);
    try {
      // Se houver valor pago, registrar em recebimentos primeiro
      if ((deliveryPaymentAmount ?? 0) > 0 && orderDb?.id) {
        console.log(`💰 [handleConfirmDeliveryPayment] Registrando recebimento de R$ ${deliveryPaymentAmount?.toFixed(2)}...`);
        const { criarRecebimento } = await import("@/integrations/supabase/recebimentos-pedidos");
        const recebimentoResult = await criarRecebimento({
          order_id: orderDb.id,
          valor_recebido: deliveryPaymentAmount || 0,
          data_recebimento: new Date().toISOString().split('T')[0],
          forma_pagamento_id: null,
          observacoes: 'Recebimento ao marcar como entregue',
        });
        if (!recebimentoResult.ok) {
          console.error(`❌ [handleConfirmDeliveryPayment] Erro ao registrar recebimento:`, recebimentoResult.error);
          toast.error("Erro ao registrar recebimento");
          setIsRegisteringDeliveryPayment(false);
          return;
        }
        console.log(`✅ [handleConfirmDeliveryPayment] Recebimento registrado com sucesso:`, recebimentoResult.data);
      } else {
        console.log(`⏭️  [handleConfirmDeliveryPayment] Nenhum valor de recebimento (0 ou vazio)`);
      }

      // Agora atualizar status para ENTREGUE
      console.log(`📊 [handleConfirmDeliveryPayment] Atualizando status do pedido para ENTREGUE...`);
      setNewStatus('ENTREGUE');
      const result = await updateOrderStatus(code, 'Entregue');
      if (!result.ok) {
        console.error(`❌ [handleConfirmDeliveryPayment] Erro ao atualizar status:`, result.error);
        toast.error(result.error || "Erro ao atualizar status");
        setIsRegisteringDeliveryPayment(false);
        return;
      }
      console.log(`✅ [handleConfirmDeliveryPayment] Status atualizado para ENTREGUE`);

      if (orderDb?.id && productionTimeline) {
      }

      toast.success("Pedido marcado como entregue!");
      setIsDeliveryPaymentOpen(false);
      setDeliveryPaymentAmount(null);
      setNewStatus("");
      setIsStatusDialogOpen(false);

      // Invalidar queries para sincronizar com outros módulos
      console.log(`🔄 [handleConfirmDeliveryPayment] Invalidando queries para sincronizar dados...`);
      await queryClient.invalidateQueries({ queryKey: ["order", code] });
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      await queryClient.invalidateQueries({ queryKey: ["receitas"] });
      console.log(`✅ [handleConfirmDeliveryPayment] Queries invalidadas. Refazendo fetch...`);
      refetch();

      console.log(`🎉 [handleConfirmDeliveryPayment] Fluxo completado com sucesso!`);
    } catch (error) {
      console.error("❌ [handleConfirmDeliveryPayment] Erro geral:", error);
      toast.error("Erro ao registrar recebimento");
    } finally {
      setIsRegisteringDeliveryPayment(false);
    }
  };

  const handleUpdatePaid = async () => {
    const value = parseFloat(newPaidValue);
    if (isNaN(value) || value < 0) {
      toast.error("Digite um valor válido");
      return;
    }

    try {
      // Atualizando valor pago do pedido
      const result = await updateOrderStatus(code, undefined, value);
      if (result.ok) {
        toast.success("Valor pago atualizado com sucesso!");
        setIsPaidDialogOpen(false);
        setNewPaidValue(""); // Limpar o campo
        
        // Se temos dados atualizados, usar eles diretamente
        if (result.data) {
          // Usando dados atualizados
          // Atualizar o cache do React Query com os dados retornados
          queryClient.setQueryData(["order", code], result.data);
          
          // Forçar re-render imediato
          await queryClient.invalidateQueries({ queryKey: ["order", code] });
          await queryClient.invalidateQueries({ queryKey: ["orders"] });
          await queryClient.invalidateQueries({ queryKey: ["receitas"] }); // Sincronizar receitas
          await queryClient.invalidateQueries({ queryKey: ["dashboard"] }); // Sincronizar dashboard
          invalidateRelated('receitas'); // Sincronizar via contexto
          
          // Refetch para garantir consistência
          refetch();
        } else {
          // Se não temos dados, forçar refetch completo
          await queryClient.invalidateQueries({ queryKey: ["order", code] });
          await queryClient.invalidateQueries({ queryKey: ["orders"] });
          await queryClient.invalidateQueries({ queryKey: ["receitas"] }); // Sincronizar receitas
          await queryClient.invalidateQueries({ queryKey: ["dashboard"] }); // Sincronizar dashboard
          invalidateRelated('receitas'); // Sincronizar via contexto
          refetch();
        }
      } else {
        console.error("Erro ao atualizar valor pago:", result.error);
        toast.error(result.error || "Erro ao atualizar valor pago");
      }
    } catch (error) {
      console.error("Erro ao atualizar valor pago:", error);
      toast.error("Erro ao atualizar valor pago");
    }
  };

  const handleSaveObservation = useCallback(async (stepKey: string) => {
    if (!orderDb?.id) return;
    setIsSavingObs(true);
    try {
      // Verificar se é etapa de produção customizada ou status
      if (productionTimeline?.steps.some(s => s.key === stepKey)) {
        // É etapa de produção (customizada)
        const result = await updateProductionStep(orderDb.id, stepKey, { notes: editingObsValue });
        if (result.ok && productionTimeline) {
          const updatedSteps = productionTimeline.steps.map((s) =>
            s.key === stepKey ? { ...s, notes: editingObsValue } : s
          );
          setProductionTimeline({ ...productionTimeline, steps: updatedSteps });
          setEditingObsKey(null);
          setEditingObsValue("");
          toast.success("Observação salva!");
        } else {
          toast.error("Erro ao salvar observação");
        }
      } else {
        // É etapa de status
        const updatedNotes = {
          ...statusNotesMap,
          [stepKey]: editingObsValue,
        };
        setStatusNotesMap(updatedNotes);

        // Salvar no banco
        if (orderDb?.id) {
          const result = await saveStatusNotes(orderDb.id, updatedNotes);
          if (!result.ok) {
            toast.error("Erro ao salvar observação");
          } else {
            toast.success("Observação salva!");
          }
        }

        setEditingObsKey(null);
        setEditingObsValue("");
      }
    } catch (error) {
      console.error("Erro:", error);
      toast.error("Erro ao salvar observação");
    } finally {
      setIsSavingObs(false);
    }
  }, [orderDb?.id, editingObsValue, productionTimeline]);

  // Handler para adicionar nova etapa
  const handleAddNewStep = useCallback(async () => {
    if (!orderDb?.id || !newStepLabel.trim()) {
      toast.error("Digite um nome para a etapa");
      return;
    }

    if (isSavingNewStep) {
      return;
    }

    setIsSavingNewStep(true);
    try {
      const result = await addProductionStep(orderDb.id, {
        label: newStepLabel.trim(),
        description: newStepDescription.trim(),
        notes: "",
        icon: newStepIcon,
        color: newStepColor,
      });
      if (result.ok) {
        const updatedTimeline = await getProductionTimeline(orderDb.id);
        setProductionTimeline(updatedTimeline);
        setEditingObsKey(null);
        setNewStepLabel("");
        setNewStepDescription("");
        setNewStepColor("blue");
        setNewStepIcon("CheckCircle");
        toast.success("Etapa adicionada com sucesso!");
      } else {
        toast.error(result.error || "Erro ao adicionar etapa");
      }
    } catch (error) {
      console.error("🔴 Erro capturado:", error);
      toast.error("Erro ao adicionar etapa");
    } finally {
      setIsSavingNewStep(false);
    }
  }, [orderDb?.id, productionTimeline, newStepLabel, newStepDescription, newStepIcon, newStepColor]);

  const handleSaveEditStep = useCallback(async () => {
    if (!orderDb?.id || !editingStepKey || !editingStepLabel.trim()) {
      toast.error("Digite um nome para a etapa");
      return;
    }
    setIsSavingEditStep(true);
    try {
      const result = await updateProductionStep(orderDb.id, editingStepKey, {
        label: editingStepLabel.trim(),
        description: editingStepDescription.trim(),
        icon: editingStepIcon,
        color: editingStepColor,
      });
      if (result.ok) {
        const updatedSteps = productionTimeline!.steps.map((s) =>
          s.key === editingStepKey
            ? {
                ...s,
                label: editingStepLabel.trim(),
                description: editingStepDescription.trim(),
                icon: editingStepIcon,
                color: editingStepColor,
              }
            : s
        );
        setProductionTimeline({ ...productionTimeline!, steps: updatedSteps });
        setEditingStepKey(null);
        setEditingStepLabel("");
        setEditingStepDescription("");
        setEditingStepIcon("CheckCircle");
        setEditingStepColor("blue");
        toast.success("Etapa editada com sucesso!");
      } else {
        toast.error(result.error || "Erro ao editar etapa");
      }
    } catch (error) {
      console.error("Erro ao editar etapa:", error);
      toast.error("Erro ao editar etapa");
    } finally {
      setIsSavingEditStep(false);
    }
  }, [orderDb?.id, editingStepKey, editingStepLabel, editingStepDescription, editingStepIcon, editingStepColor, productionTimeline]);

  const handleLimparNotasComErro = useCallback(async () => {
    const notasComErro = notasFiscais.filter((nota) =>
      ["erro_emissao", "erro_autorizacao", "erro_validacao", "erro_validacao_schema", "denegado"].includes(nota.status)
    );

    if (notasComErro.length === 0) {
      toast.info("Não há notas com erro para limpar neste pedido.");
      return;
    }

    const confirmar = window.confirm(
      `Limpar ${notasComErro.length} nota(s) com erro desta tela do pedido?\n\n` +
      "Isso NÃO cancela nem exclui a nota na Focus; apenas remove o vínculo com este pedido."
    );

    if (!confirmar) return;

    try {
      setLimpandoNotasErro(true);
      const focusNF = await getFocusNFService();
      if (!focusNF) {
        toast.error("Serviço de notas fiscais indisponível");
        return;
      }

      const result = await focusNF.limparNotasComErroDoPedido(code);
      if (!result.ok) {
        toast.error(result.error || "Erro ao limpar notas com erro");
        return;
      }

      await loadNotasFiscais();
      toast.success(`${result.count || 0} nota(s) com erro removida(s) desta visualização.`);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao limpar notas com erro");
    } finally {
      setLimpandoNotasErro(false);
    }
  }, [code, getFocusNFService, loadNotasFiscais, notasFiscais]);

  const hasNotaAutorizada = notasFiscais.some(
    (nota) => nota.status === 'autorizado' || nota.status === 'autorizada'
  );

  const notasComErroCount = notasFiscais.filter((nota) =>
    ['erro_emissao', 'erro_autorizacao', 'erro_validacao', 'erro_validacao_schema', 'denegado'].includes(nota.status)
  ).length;

  const handleUpdateDescription = async () => {
    if (!newDescription.trim()) {
      toast.error("Digite uma descrição válida");
      return;
    }

    try {
      // Atualizando descrição do pedido
      const result = await updateOrderStatus(code, undefined, undefined, newDescription);
      if (result.ok) {
        toast.success("Descrição atualizada com sucesso!");
        setNewDescription(""); // Limpar o campo

        // Forçar refetch para atualizar a interface
        await queryClient.invalidateQueries({ queryKey: ["order", code] });
        await queryClient.invalidateQueries({ queryKey: ["orders"] });
        await queryClient.invalidateQueries({ queryKey: ["receitas"] }); // Sincronizar receitas
        invalidateRelated('receitas'); // Sincronizar via contexto
        refetch();
      } else {
        console.error("Erro ao atualizar descrição:", result.error);
        toast.error(result.error || "Erro ao atualizar descrição");
      }
    } catch (error) {
      console.error("Erro ao atualizar descrição:", error);
      toast.error("Erro ao atualizar descrição");
    }
  };

  const handleUpdatePaymentMethod = async () => {
    if (!paymentMethod.trim() || paymentMethod === "none") {
      toast.error("Selecione uma forma de pagamento válida");
      return;
    }

    try {
      // Buscar o nome da forma de pagamento selecionada
      const forma = formasPagamento.find(f => f.id === paymentMethod);
      const nomeForma = forma?.nome || paymentMethod;

      // Atualizar no banco
      const { supabase } = await import('@/integrations/supabase/client');
      const { data, error } = await supabase
        .from('atelie_orders')
        .update({ payment_method: nomeForma })
        .eq('code', code)
        .select()
        .single();

      if (error) {
        console.error("Erro ao atualizar forma de pagamento:", error);
        toast.error("Erro ao atualizar forma de pagamento");
        return;
      }

      toast.success("Forma de pagamento atualizada com sucesso!");
      setIsPaymentMethodDialogOpen(false);

      // Forçar refetch em todos os módulos afetados
      await queryClient.invalidateQueries({ queryKey: ["order", code] });
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      await queryClient.invalidateQueries({ queryKey: ["receitas"] });
      await queryClient.invalidateQueries({ queryKey: ["contas_receber"] });
      await queryClient.invalidateQueries({ queryKey: ["fluxo_caixa"] });
      await queryClient.invalidateQueries({ queryKey: ["relatorios"] });
      await queryClient.invalidateQueries({ queryKey: ["pedidos"] });
      invalidateRelated('receitas');
      invalidateRelated('contas_receber');
      invalidateRelated('fluxo_caixa');
      refetch();
    } catch (error) {
      console.error("Erro ao atualizar forma de pagamento:", error);
      toast.error("Erro ao atualizar forma de pagamento");
    }
  };

  // Query para buscar status de pagamento
  const { data: receitaData, isLoading: isLoadingPayment } = useQuery({
    queryKey: ["receita", id],
    queryFn: () => getReceitaByOrderCode(id),
    enabled: Boolean(id),
    refetchOnWindowFocus: false,
    staleTime: 0, // Sempre buscar dados frescos
  });

  // Buscar medidas do cliente
  const { data: medidas = [] } = useQuery({
    queryKey: ["medidas", empresa?.id],
    queryFn: () => getMedidas(empresa?.id || ''),
    enabled: !!empresa?.id && !!orderDb?.customer_name,
  });

  // Carregar formas de pagamento
  useEffect(() => {
    const carregarFormas = async () => {
      const formas = await listarFormasPagamento();
      setFormasPagamento(formas);
    };
    carregarFormas();
  }, []);
  
  // Função para remover duplicatas das personalizações
  const removeDuplicatePersonalizations = useCallback((personalizations: OrderRow["personalizations"] = []) => {
    if (!personalizations || personalizations.length === 0) return [];
    
    console.log('[removeDuplicatePersonalizations] Entrada:', {
      length: personalizations.length,
      items: personalizations
    });
    
    // Primeiro, tentar usar IDs únicos se disponíveis
    const seenIds = new Set<string>();
    const seenKeys = new Map<string, typeof personalizations[0]>();
    const uniqueItems: typeof personalizations = [];
    
    personalizations.forEach((item, index) => {
      // Se o item tem um ID único, usar ele para verificar duplicatas
      if (item.id) {
        if (!seenIds.has(item.id)) {
          seenIds.add(item.id);
          uniqueItems.push(item);
          console.log(`[removeDuplicatePersonalizations] Item ${index} adicionado (ID: ${item.id})`);
        } else {
        }
      } else {
        // Se não tem ID, usar uma combinação de campos como chave
        const key = `${item.person_name || ''}_${item.size || ''}_${item.quantity || 1}_${item.notes || ''}`;
        
        if (!seenKeys.has(key)) {
          seenKeys.set(key, item);
          uniqueItems.push(item);
          console.log(`[removeDuplicatePersonalizations] Item ${index} adicionado (chave: ${key})`);
        } else {
        }
      }
    });
    
    if (personalizations.length !== uniqueItems.length) {
      console.log(`[removeDuplicatePersonalizations] ✅ Removidas ${personalizations.length - uniqueItems.length} duplicatas. Original: ${personalizations.length}, Único: ${uniqueItems.length}`);
    } else {
      console.log(`[removeDuplicatePersonalizations] ✅ Nenhuma duplicata encontrada. Total: ${uniqueItems.length}`);
    }
    
    return uniqueItems;
  }, []);
  
  const order = useMemo(() => {
    if (orderDb) {
      // 🔍 Converter catalog_products se for object-array
      const catalogProductsRaw = (orderDb as any).catalog_products;
      let catalogProductsFinal = catalogProductsRaw;
      if (catalogProductsRaw && typeof catalogProductsRaw === 'object' && !Array.isArray(catalogProductsRaw)) {
        const keys = Object.keys(catalogProductsRaw);
        if (keys.every(k => /^\d+$/.test(k))) {
          catalogProductsFinal = Object.values(catalogProductsRaw);
        }
      }
      
      const personalizations = removeDuplicatePersonalizations(orderDb.personalizations);
      return {
        id: orderDb.id,
        code: orderDb.code, // ✅ Código do pedido (PED-...)​
        client: orderDb.customer_name,
        customer_phone: (orderDb as any).customer_phone || (orderDb as any).phone || (orderDb as any).telefone || undefined,
        type: orderDb.type,
        description: orderDb.description ?? "",
        value: Number(orderDb.value || 0),
        discount: (orderDb as any).discount ?? 0,
        discount_type: (orderDb as any).discount_type ?? 'fixed',
        paid: Number(orderDb.paid || 0),
        delivery: orderDb.delivery_date ?? "",
        due_date: (orderDb as any).due_date ?? undefined,
        payment_method: (orderDb as any).payment_method ?? undefined,
        status: orderDb.status,
        file: orderDb.file_url ?? undefined,
        personalizations: personalizations,
        catalog_products: catalogProductsFinal, // ✅ NOVO: Incluir catalog_products convertido
        items: (orderDb as any).items ?? [], // ✅ NOVO: Incluir itens reais do pedido
      } as OrderItem & { customer_phone?: string; code?: string; catalog_products?: any; discount?: number; discount_type?: string; due_date?: string; payment_method?: string; items?: any[] };
    }
    return null;
  }, [orderDb, forceUpdate, removeDuplicatePersonalizations]);

  // Função para determinar o status de pagamento
  const getPaymentStatus = () => {
    if (!order) return { status: "unknown", label: "Desconhecido", color: "bg-gray-100 text-gray-600" };
    
    const totalValue = order.value;
    const paidValue = order.paid;
    
    if (paidValue === 0) {
      return { status: "pending", label: "Pendente", color: "bg-yellow-100 text-yellow-800" };
    } else if (paidValue >= totalValue) {
      return { status: "paid", label: "Pago", color: "bg-green-100 text-green-800" };
    } else {
      return { status: "partial", label: "Parcial", color: "bg-blue-100 text-blue-800" };
    }
  };

  const paymentStatus = getPaymentStatus();

  // Função para gerar Ficha Técnica para Funcionários (sem valores financeiros)
  const generateEmployeeTechnicalSheet = () => {
    console.log("=== GERANDO FICHA TÉCNICA PARA FUNCIONÁRIOS ===");
    console.log("Order:", order);
    console.log("📋 Personalizations Debug:", {
      length: order?.personalizations?.length || 0,
      data: order?.personalizations,
      stringified: JSON.stringify(order?.personalizations, null, 2)
    });
    
    // Gerar HTML da ficha técnica
    const technicalSheetHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Ficha Técnica - ${order.id}</title>
          <meta charset="utf-8">
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: 'Arial', sans-serif;
              line-height: 1.3;
              color: #1f2937;
              background: white;
              padding: 8px;
              font-size: 10px;
            }
            .container { max-width: 800px; margin: 0 auto; }
            .header {
              text-align: center;
              margin-bottom: 6px;
              border-bottom: 2px solid #34495e;
              padding-bottom: 3px;
            }
            .header h1 { font-size: 18px; font-weight: bold; margin: 0; margin-bottom: 4px; color: #34495e; }
            .header-info { display: flex; justify-content: center; gap: 20px; font-size: 9px; }
            .section {
              margin-bottom: 6px;
              background: white;
              padding: 6px;
              border: 1px solid #bdc3c7;
              border-radius: 3px;
              page-break-inside: avoid;
            }
            .section-header {
              font-size: 10px;
              font-weight: bold;
              background: #34495e;
              color: white;
              padding: 4px 5px;
              margin-bottom: 4px;
              text-transform: uppercase;
            }
            .section-content { padding: 0 2px; }
            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 6px;
              margin-bottom: 4px;
            }
            .info-item {
              display: flex;
              flex-direction: column;
              font-size: 9px;
            }
            .info-label {
              font-size: 8px;
              color: #7f8c8d;
              font-weight: bold;
              text-transform: uppercase;
              margin-bottom: 1px;
            }
            .info-value {
              font-size: 9px;
              color: #1f2937;
              font-weight: 500;
            }
            .status-badge {
              display: inline-block;
              padding: 3px 8px;
              border-radius: 3px;
              font-size: 9px;
              font-weight: bold;
              background: #ecf0f1;
              color: #34495e;
            }
            .logo-section {
              text-align: center;
              padding: 6px;
              background: #fafafa;
              border: 1px solid #bdc3c7;
            }
            .logo-section img {
              max-width: 100%;
              max-height: 120px;
              border: 1px solid #bdc3c7;
              border-radius: 3px;
            }
            .checklist {
              background: white;
              padding: 4px 6px;
            }
            .checklist ul {
              list-style: none;
              padding: 0;
              margin: 0;
            }
            .checklist li {
              margin-bottom: 2px;
              display: flex;
              align-items: center;
              font-size: 10px;
            }
            .checklist li span {
              color: #34495e;
              margin-right: 4px;
              font-weight: bold;
              font-size: 11px;
            }
            .personal-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 2px;
              font-size: 9px;
            }
            .personal-table th,
            .personal-table td {
              border: 1px solid #bdc3c7;
              padding: 3px 4px;
              text-align: left;
            }
            .personal-table th {
              background: #34495e;
              color: white;
              text-transform: uppercase;
              font-weight: bold;
              font-size: 8px;
            }
            .personal-table td:first-child {
              font-weight: 600;
            }
            .footer {
              margin-top: 4px;
              text-align: center;
              font-size: 8px;
              color: #7f8c8d;
              border-top: 1px solid #bdc3c7;
              padding-top: 2px;
            }
            .employee-notice {
              background: #ecf0f1;
              border-left: 4px solid #34495e;
              padding: 4px 6px;
              margin-bottom: 6px;
              text-align: center;
            }
            .employee-notice h3 {
              color: #34495e;
              font-size: 10px;
              margin-bottom: 1px;
              font-weight: bold;
            }
            .employee-notice p {
              color: #34495e;
              font-size: 9px;
            }
            @media print {
              body { padding: 0; }
              .container { max-width: none; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>FICHA TÉCNICA DE PRODUÇÃO</h1>
              <div class="header-info">
                <div><strong>Cód:</strong> ${order.id}</div>
                <div><strong>Data:</strong> ${new Date().toLocaleDateString('pt-BR')}</div>
              </div>
            </div>

            <div class="employee-notice">
              <h3>DOCUMENTO INTERNO PARA FUNCIONÁRIOS</h3>
              <p>Contém apenas informações técnicas necessárias para a produção</p>
            </div>

            <div class="section">
              <div class="section-header">INFORMAÇÕES DO PEDIDO E CLIENTE</div>
              <div class="section-content">
                <div class="info-grid">
                  <div class="info-item">
                    <div class="info-label">Código</div>
                    <div class="info-value">${order.id}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Cliente</div>
                    <div class="info-value">${order.client}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Tipo</div>
                    <div class="info-value">${order.type}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Entrega</div>
                    <div class="info-value">${order.delivery ? (() => {
                      const [year, month, day] = order.delivery.split('T')[0].split('-');
                      return day + '/' + month + '/' + year;
                    })() : 'N/A'}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Telefone</div>
                    <div class="info-value">${order.customer_phone || '—'}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">CPF/CNPJ</div>
                    <div class="info-value">${(order as any).customer_cpf_cnpj || '—'}</div>
                  </div>
                </div>
              </div>
            </div>

            ${(order.items && order.items.length > 0) || order.description ? `
            <div class="section">
              <div class="section-header">ITENS DO PEDIDO</div>
              <div class="section-content">
                <table class="personal-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th style="width: 20%;">Qtd</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${order.items && order.items.length > 0
                      ? order.items.map((item: any) => `
                      <tr>
                        <td>${item.description || 'Produto/Serviço'}</td>
                        <td style="text-align: center;">${item.quantity || 1}</td>
                      </tr>
                      ${item.especificacoes && typeof item.especificacoes === 'object' && Object.keys(item.especificacoes).length > 0 ? `
                      <tr style="background-color: #f9f9f9;">
                        <td colspan="2" style="padding: 8px 5px; font-size: 12px;">
                          <strong>Especificações:</strong> ${item.category ? `Categoria: ${item.category} | ` : ''}${Object.entries(item.especificacoes || {}).map(([key, value]) => value ? `${key}: ${String(value)}` : null).filter(Boolean).join(' | ')}
                        </td>
                      </tr>
                      ` : ''}
                    `).join('')
                      : order.description.split('\\n').filter((line: string) => line.trim()).map((line: string) => `
                      <tr>
                        <td>${line.trim()}</td>
                        <td style="text-align: center;">—</td>
                      </tr>
                    `).join('')
                    }
                  </tbody>
                </table>
              </div>
            </div>
            ` : ''}

            ${order.personalizations && order.personalizations.length ? `
            <div class="section">
              <div class="section-header">PERSONALIZAÇÕES INDIVIDUAIS</div>
              <div class="section-content">
                <table class="personal-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Tamanho</th>
                      <th>Qtd</th>
                      <th>Observações</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${order.personalizations.map((item: any) => `
                      <tr>
                        <td>${item.person_name || ''}</td>
                        <td>${item.size || '—'}</td>
                        <td style="text-align: center;">${item.quantity ?? 1}</td>
                        <td>${item.notes || ''}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </div>
            ` : ''}
            
            ${(() => {
              const medidasCliente = medidas.filter(medida =>
                medida.cliente_nome.toLowerCase().includes(order.client.toLowerCase())
              );

              if (medidasCliente.length === 0) return '';

              return `
                <div class="section">
                  <div class="section-header">MEDIDAS DO CLIENTE</div>
                  <div class="section-content">
                    ${medidasCliente.map(medida => `
                      <div style="margin-bottom: 4px; padding: 3px; background: #fafafa; border: 1px solid #bdc3c7;">
                        <div style="font-weight: bold; color: #34495e; margin-bottom: 2px; font-size: 9px;">
                          ${medida.tipo_peca.toUpperCase()}
                        </div>
                        <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 2px; font-size: 8px; line-height: 1.2;">
                          ${medida.busto ? `<div><strong>Busto:</strong> ${medida.busto}cm</div>` : ''}
                          ${medida.cintura ? `<div><strong>Cin:</strong> ${medida.cintura}cm</div>` : ''}
                          ${medida.quadril ? `<div><strong>Quad:</strong> ${medida.quadril}cm</div>` : ''}
                          ${medida.ombro ? `<div><strong>Ombro:</strong> ${medida.ombro}cm</div>` : ''}
                          ${medida.largura_costas ? `<div><strong>L.Cos:</strong> ${medida.largura_costas}cm</div>` : ''}
                          ${medida.cava_manga ? `<div><strong>Cava:</strong> ${medida.cava_manga}cm</div>` : ''}
                          ${medida.comprimento_manga ? `<div><strong>C.Mng:</strong> ${medida.comprimento_manga}cm</div>` : ''}
                          ${medida.comprimento ? `<div><strong>Comp:</strong> ${medida.comprimento}cm</div>` : ''}
                          ${medida.pescoco ? `<div><strong>Pesc:</strong> ${medida.pescoco}cm</div>` : ''}
                        </div>
                        ${medida.observacoes ? `
                          <div style="margin-top: 2px; font-size: 8px; color: #34495e;">
                            <strong>Obs:</strong> ${medida.observacoes}
                          </div>
                        ` : ''}
                      </div>
                    `).join('')}
                  </div>
                </div>
              `;
            })()}
            
            ${arquivos.length > 0 ? `
            <div class="section">
              <div class="section-header">ARTES / ARQUIVOS DO PEDIDO</div>
              <div class="section-content">
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 8px;">
                  ${arquivos.map((arquivo: ArquivoData) => `
                    <div style="text-align: center; padding: 6px; background: #fafafa; border: 1px solid #bdc3c7; border-radius: 3px;">
                      <div style="font-weight: bold; font-size: 8px; margin-bottom: 3px; color: #2c3e50;">${arquivo.posicao}</div>
                      ${arquivo.url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) ? `
                        <img src="${arquivo.url}" alt="${arquivo.nome}" style="max-width: 100%; max-height: 80px; border: 1px solid #bdc3c7; border-radius: 2px; margin-bottom: 3px;" />
                      ` : `
                        <div style="background: #e8e8e8; padding: 8px; border-radius: 2px; margin-bottom: 3px; font-size: 8px;">📄</div>
                      `}
                      <div style="font-size: 7px; color: #7f8c8d; word-break: break-word;">
                        ${arquivo.nome}
                      </div>
                      ${arquivo.descricao ? `
                        <div style="font-size: 7px; color: #555; margin-top: 2px;">
                          ${arquivo.descricao}
                        </div>
                      ` : ''}
                    </div>
                  `).join('')}
                </div>
              </div>
            </div>
            ` : order.file ? `
            <div class="section">
              <div class="section-header">ARTE/DESIGN DO PEDIDO</div>
              <div class="section-content">
                <div class="logo-section">
                  <img src="${order.file}" alt="Arte do Pedido" />
                  <p style="margin-top: 4px; font-size: 9px; color: #7f8c8d;">Arte anexada pelo cliente</p>
                </div>
              </div>
            </div>
            ` : ''}

            <div class="section">
              <div class="section-header">DESCRIÇÃO TÉCNICA E CHECKLIST</div>
              <div class="section-content">
                <div style="display: flex; gap: 8px;">
                  <div style="flex: 1;">
                    <div style="font-weight: bold; color: #34495e; margin-bottom: 4px; font-size: 10px;">ESPECIFICAÇÕES</div>
                    <div style="border: 1px solid #bdc3c7; padding: 4px 6px; background: #fafafa; font-size: 9px; max-height: 70px; overflow-y: auto; line-height: 1.3;">
                      ${order.description || 'Sem descrição'}
                    </div>
                  </div>
                  <div style="flex: 1;">
                    <div style="font-weight: bold; color: #34495e; margin-bottom: 4px; font-size: 10px;">VERIFICAÇÕES</div>
                    <div class="checklist" style="border: 1px solid #bdc3c7;">
                      <ul style="columns: 1; column-gap: 6px;">
                        <li><span>☐</span> Verificar especificações</li>
                        <li><span>☐</span> Confirmar materiais</li>
                        <li><span>☐</span> Executar produção</li>
                        <li><span>☐</span> Controle de qualidade</li>
                        <li><span>☐</span> Empacotar</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="section">
              <div class="section-header">ANOTAÇÕES E ASSINATURA</div>
              <div class="section-content">
                <div style="border: 1px solid #bdc3c7; padding: 3px 4px; background: #fafafa; line-height: 14px; min-height: 35px;">
                  <div style="height: 12px; border-bottom: 1px solid #d1d5db; margin-bottom: 1px;"></div>
                  <div style="height: 12px; border-bottom: 1px solid #d1d5db; margin-bottom: 1px;"></div>
                </div>
                <div style="font-size: 8px; margin-top: 3px; display: flex; justify-content: space-between;">
                  <div>Responsável: _____________________</div>
                  <div>Data: ____/____/____</div>
                </div>
              </div>
            </div>

            <div class="footer">
              <p>Gerado em ${new Date().toLocaleString('pt-BR')} - Ateliê Pro | Documento interno - Sem informações financeiras</p>
            </div>
          </div>
        </body>
      </html>
    `;
    
    // Abrir nova janela com a ficha técnica
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(technicalSheetHtml);
      newWindow.document.close();
      
      // Aguardar carregamento e abrir diálogo de impressão
      newWindow.onload = () => {
        console.log("Ficha técnica carregada, abrindo diálogo de impressão...");
        newWindow.print();
      };
    } else {
      toast.error("Não foi possível abrir a janela. Verifique se os pop-ups estão bloqueados.");
    }
  };

  // Função para gerar Cupom (Não Fiscal) - Otimizado para impressora térmica
  const generateCupomFiscal = (
    via: 'cliente' | 'estabelecimento' | 'ambas' = 'cliente',
    options?: { mostrarAvisoBoas?: boolean; mostrarAvisoPromocao?: boolean; observacaoCustomizada?: string },
    printerType: 'thermica' | 'a4' = 'thermica'
  ) => {
    if (!order || !empresa) {
      toast.error("Dados insuficientes para gerar cupom");
      return;
    }

    const finalOptions = {
      mostrarAvisoBoas: Boolean(options?.mostrarAvisoBoas ?? true),
      mostrarAvisoPromocao: Boolean(options?.mostrarAvisoPromocao ?? true),
      observacaoCustomizada: options?.observacaoCustomizada ?? '',
    };

    // Formatar CNPJ
    const formatCNPJ = (cnpj: string | undefined) => {
      if (!cnpj) return "";
      const cleaned = cnpj.replace(/\D/g, "");
      if (cleaned.length === 14) {
        return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8, 12)}-${cleaned.slice(12)}`;
      }
      return cnpj;
    };

    // Formatar telefone
    const formatPhone = (phone: string | undefined) => {
      if (!phone) return "";
      const cleaned = phone.replace(/\D/g, "");
      if (cleaned.length === 11) {
        return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
      } else if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
      }
      return phone;
    };

    // Formatar valor monetário
    const formatCurrency = (value: number) => {
      return new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    };

    const now = new Date();
    const dateStr = now.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const timeStr = now.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });

    // Gerar número do cupom (usar código do pedido ou timestamp)
    const cupomNumber = order.code || order.id || now.getTime().toString().slice(-6);

    // Calcular valores
    const subtotal = order.value;
    const orderDiscount = (order as any)?.discount ?? 0;
    const orderDiscountType = (order as any)?.discount_type ?? 'fixed';
    const discountAmount = orderDiscountType === 'percentage'
      ? (subtotal * orderDiscount / 100)
      : orderDiscount;
    const total = Math.max(0, subtotal - discountAmount);
    const paid = order.paid;
    const remaining = total - paid;

    // Função para gerar o conteúdo do cupom (via cliente ou estabelecimento)
    const gerarConteudoCupom = (tipoVia: 'cliente' | 'estabelecimento', opts?: { mostrarAvisoBoas?: boolean; mostrarAvisoPromocao?: boolean; observacaoCustomizada?: string }, printer: 'thermica' | 'a4' = 'thermica') => {
      const tituloVia = tipoVia === 'cliente' ? 'VIA CLIENTE' : 'VIA ESTABELECIMENTO';
      const viaCupomOptions = {
        mostrarAvisoBoas: Boolean(opts?.mostrarAvisoBoas ?? true),
        mostrarAvisoPromocao: Boolean(opts?.mostrarAvisoPromocao ?? true),
        observacaoCustomizada: opts?.observacaoCustomizada ?? '',
      };

      return `
      <div class="cupom-via">
        <div class="via-label">${tituloVia}</div>
        <div class="header">
          <h1>${empresa.nome || "ATELIÊ PRO"}</h1>
          ${empresa.endereco ? `<p>${empresa.endereco}</p>` : ""}
          ${empresa.cpf_cnpj ? `<p>CNPJ: ${formatCNPJ(empresa.cpf_cnpj)}</p>` : ""}
          ${empresa.telefone ? `<p>IE: ${formatPhone(empresa.telefone)}</p>` : ""}
        </div>

        <div class="fiscal-notice">
          NAO E DOCUMENTO FISCAL
        </div>

        <div class="info-section">
          <div class="info-line">
            <span class="info-label">VENDA SIMPLES</span>
          </div>
          <div class="info-line">
            <span>Numero: ${cupomNumber}</span>
          </div>
          <div class="info-line">
            <span>${dateStr} ${timeStr}</span>
          </div>
        </div>

        <div class="customer-section">
          <div class="info-line">
            <span class="info-label">Cliente</span>
          </div>
          <div class="info-line">
            <span>
              ${order.client || "CONSUMIDOR"}
              ${order.customer_phone ? ` <span style='color:#888;font-size:14px;'>( ${order.customer_phone} )</span>` : " <span style='color:#888;font-size:14px;'>(Telefone não informado)</span>"}
            </span>
          </div>
        </div>

        <div class="item-section">
          <div class="item-header">
            <span class="item-number">###</span>
            <span class="item-description">DESCRICAO</span>
            <span class="item-code">COD</span>
            <span class="item-quantity">QTD UN</span>
            <span class="item-value">VL ITEM</span>
          </div>
          ${order.items && order.items.length > 0
            ? order.items.map((item: any, idx: number) => {
                const itemNumber = String(idx + 1).padStart(3, '0');
                const itemDesc = (item.description || item.type || "PRODUTO/SERVIÇO").toUpperCase();
                const itemQty = item.quantity || 1;
                const itemValue = item.unit_value || (order.value / (order.items?.length || 1));
                return `
          <div class="item-row">
            <span class="item-number">${itemNumber}</span>
            <span class="item-description">${itemDesc}</span>
            <span class="item-code">${item.id?.slice(0, 5) || order.id.slice(0, 5)}</span>
            <span class="item-quantity">${itemQty},000 UN</span>
            <span class="item-value">${formatCurrency(itemValue)}</span>
          </div>
                `;
              }).join('')
            : `
          <div class="item-row">
            <span class="item-number">001</span>
            <span class="item-description">${(order.description || order.type || "PRODUTO/SERVIÇO").toUpperCase()}</span>
            <span class="item-code">${order.id.slice(0, 5)}</span>
            <span class="item-quantity">1,000 UN</span>
            <span class="item-value">${formatCurrency(order.value)}</span>
          </div>
            `
          }
        </div>

        <div class="summary">
          <div class="summary-line">
            <span>VALOR ORIGINAL</span>
            <span>${formatCurrency(subtotal)}</span>
          </div>
          ${orderDiscount > 0 ? `
          <div class="summary-line">
            <span>DESCONTO ${orderDiscountType === 'percentage' ? `(${orderDiscount.toFixed(2)}%)` : '(FIXO)'}</span>
            <span>-${formatCurrency(discountAmount)}</span>
          </div>
          ` : ""}
          <div class="summary-line summary-total">
            <span>VALOR A PAGAR</span>
            <span>${formatCurrency(total)}</span>
          </div>
          ${paid > 0 ? `
          <div class="summary-line">
            <span>VALOR RECEBIDO</span>
            <span>${formatCurrency(paid)}</span>
          </div>
          ` : ""}
          <div class="summary-line">
            <span>SALDO DEVEDOR</span>
            <span>${formatCurrency(Math.max(0, remaining))}</span>
          </div>
        </div>

        <div class="footer">
          <p>PDU No.: 1</p>
          <p>Operador: Sistema</p>
          ${empresa.responsavel ? `<p>Vendedor(a): ${empresa.responsavel.toUpperCase()}</p>` : ""}
        </div>

        <div class="legal-notice">
          <p>Sonegar e Crime! Quem paga por ele? Voce!</p>
          <p>Sua unica defesa: Exija a nota fiscal</p>
        </div>

        <div class="contact">
          ${empresa.telefone ? `<p>Telefone: ${formatPhone(empresa.telefone)}</p>` : ""}
        </div>

        ${viaCupomOptions.mostrarAvisoBoas || viaCupomOptions.mostrarAvisoPromocao || viaCupomOptions.observacaoCustomizada ? `
          <div class="policies" style="margin-top: 12px; text-align: center; border-top: 1px dashed #000; padding-top: 8px;">
            ${viaCupomOptions.mostrarAvisoBoas ? '<p style="font-size: 11px; font-weight: bold; margin: 4px 0;">NÃO EFETUAMOS TROCAS EM DIAS DE LIQUIDACAO</p>' : ''}
            ${viaCupomOptions.mostrarAvisoPromocao ? '<p style="font-size: 11px; font-weight: bold; margin: 4px 0;">NÃO EFETUAMOS TROCAS DE PEÇAS NA PROMOCAO</p>' : ''}
            ${viaCupomOptions.observacaoCustomizada ? `<p style="font-size: 11px; margin: 8px 0; font-style: italic; white-space: pre-wrap;">${viaCupomOptions.observacaoCustomizada}</p>` : ''}
          </div>
        ` : ''}
      </div>
      `;
    };

    // Gerar CSS baseado no tipo de impressora
    const getStyles = (type: 'thermica' | 'a4') => {
      if (type === 'a4') {
        return `
          body {
            font-family: 'Arial', sans-serif;
            font-size: 11px;
            line-height: 1.4;
            color: #333333 !important;
            background: white;
            padding: 50px 70px;
            width: 100%;
            margin: 0 auto;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .header { text-align: center; margin-bottom: 12px; border-bottom: 2px solid #000; padding-bottom: 8px; }
          .header h1 { font-size: 18px; font-weight: 700; margin-bottom: 3px; color: #000 !important; }
          .header p { font-size: 10px; color: #333 !important; margin: 2px 0; }
          .fiscal-notice { font-size: 10px; padding: 6px; background: #fff3cd; border-left: 3px solid #ffc107; margin: 8px 0; font-weight: 600; color: #333 !important; }
          .info-section { margin-bottom: 8px; padding: 5px 0; }
          .info-line { font-size: 10px; margin: 1px 0; }
          .customer-section { background: #f5f5f5; padding: 6px 8px; margin: 8px 0; font-size: 10px; }
          .item-section { margin: 8px 0; }
          .item-header { background: #e0e0e0; color: #000 !important; padding: 5px 4px; font-weight: 700; font-size: 9px; display: grid; grid-template-columns: 30px 1fr 50px 50px 70px; gap: 4px; }
          .item-row { display: grid; grid-template-columns: 30px 1fr 50px 50px 70px; gap: 4px; padding: 4px 4px; border-bottom: 1px solid #ddd; font-size: 10px; align-items: start; }
          .item-number { font-weight: 600; color: #000 !important; font-size: 9px; }
          .item-description { font-weight: 500; color: #000 !important; word-wrap: break-word; font-size: 10px; }
          .item-code { text-align: center; font-size: 8px; color: #666 !important; }
          .item-quantity { text-align: center; font-weight: 600; font-size: 9px; }
          .item-value { text-align: right; font-weight: 600; color: #000 !important; font-size: 10px; }
          .summary { margin: 8px 0; padding: 8px; background: #f9f9f9; border: 1px solid #ddd; }
          .summary-line { display: flex; justify-content: space-between; padding: 3px 0; font-size: 10px; color: #333 !important; }
          .summary-line span:first-child { font-weight: 600; }
          .summary-total { display: flex; justify-content: space-between; font-size: 12px; font-weight: 700; padding: 6px 0; color: #000 !important; border-top: 1px solid #000; border-bottom: 1px solid #000; margin: 6px 0; }
          .payment-info { margin: 8px 0; padding: 6px; background: #f0f0f0; border-left: 3px solid #999; }
          .payment-line { display: flex; justify-content: space-between; padding: 2px 0; font-weight: 500; color: #333 !important; font-size: 9px; }
          .footer { text-align: center; margin-top: 10px; padding-top: 8px; border-top: 1px solid #ddd; font-size: 9px; }
          .policies { margin: 6px 0; padding: 6px; background: #f5f5f5; border: 1px solid #ddd; font-size: 9px; line-height: 1.3; }
          .legal-notice { margin-top: 8px; padding: 6px; text-align: center; font-size: 8px; font-style: italic; color: #666 !important; }
          .contact { font-size: 9px; text-align: center; margin-top: 6px; }
          @media print { body { padding: 10px; margin: 0; } }
        `;
      }
      // Térmica (80mm)
      return `
          body {
            font-family: 'Courier New', 'Courier', monospace;
            font-size: 13px;
            line-height: 1.4;
            color: #000000 !important;
            background: white;
            padding: 5px;
            width: 80mm;
            max-width: 80mm;
            margin: 0 auto;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        `;
    };

    // Gerar HTML do cupom otimizado
    const cupomHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Cupom - ${order.code || order.id}</title>
          <meta charset="utf-8">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            ${getStyles(printerType)}
            * {
              color: #000000 !important;
            }
            .via-label {
              text-align: center;
              font-weight: bold;
              font-size: 11px;
              margin-bottom: 4px;
              padding: 2px 0;
              border-bottom: 1px dashed #000000;
              color: #000000 !important;
              text-transform: uppercase;
            }
            .cupom-via {
              margin-bottom: 8px;
            }
            .cupom-via + .separator {
              text-align: center;
              margin: 4px 0;
              border-top: 1px dashed #000000;
              padding: 4px 0;
              font-weight: bold;
              font-size: 10px;
              color: #000000 !important;
            }
            .header {
              text-align: center;
              margin-bottom: 8px;
            }
            .header h1 {
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 4px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              color: #000000 !important;
            }
            .header p {
              font-size: 11px;
              margin: 1px 0;
              color: #000000 !important;
            }
            .fiscal-notice {
              text-align: center;
              font-weight: bold;
              font-size: 12px;
              margin: 6px 0;
              padding: 4px 0;
              border-top: 2px solid #000000;
              border-bottom: 2px solid #000000;
              color: #000000 !important;
            }
            .info-section {
              margin-bottom: 6px;
              text-align: center;
            }
            .info-line {
              font-size: 12px;
              margin: 2px 0;
              color: #000000 !important;
            }
            .info-label {
              font-weight: bold;
              text-transform: uppercase;
              color: #000000 !important;
            }
            .customer-section {
              margin: 6px 0;
              text-align: center;
              font-size: 12px;
            }
            .item-section {
              margin: 6px 0;
            }
            .item-header {
              display: flex;
              justify-content: space-between;
              font-size: 10px;
              font-weight: bold;
              margin-bottom: 3px;
              text-transform: uppercase;
              color: #000000 !important;
            }
            .item-row {
              font-size: 12px;
              margin: 3px 0;
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              color: #000000 !important;
            }
            .item-number {
              width: 25px;
              text-align: left;
              color: #000000 !important;
              font-weight: bold;
            }
            .item-description {
              flex: 1;
              margin: 0 3px;
              text-align: left;
              word-wrap: break-word;
              color: #000000 !important;
              font-weight: bold;
            }
            .item-code {
              width: 40px;
              text-align: center;
              color: #000000 !important;
            }
            .item-quantity {
              width: 70px;
              text-align: right;
              white-space: nowrap;
              color: #000000 !important;
              font-weight: bold;
            }
            .item-value {
              width: 60px;
              text-align: right;
              white-space: nowrap;
              color: #000000 !important;
              font-weight: bold;
            }
            .summary {
              margin-top: 6px;
              border-top: 2px solid #000000;
              padding-top: 4px;
            }
            .summary-line {
              display: flex;
              justify-content: space-between;
              font-size: 12px;
              margin: 2px 0;
              color: #000000 !important;
            }
            .summary-total {
              font-weight: bold;
              font-size: 14px;
              color: #000000 !important;
            }
            .payment-info {
              margin: 6px 0;
              padding: 4px 0;
              border-top: 2px dashed #000000;
              border-bottom: 2px dashed #000000;
            }
            .payment-line {
              font-size: 12px;
              margin: 2px 0;
              display: flex;
              justify-content: space-between;
              color: #000000 !important;
              font-weight: bold;
            }
            .footer {
              margin-top: 8px;
              text-align: center;
              font-size: 11px;
            }
            .footer p {
              margin: 2px 0;
              color: #000000 !important;
            }
            .policies {
              margin: 6px 0;
              font-size: 10px;
              padding: 4px 0;
              border-top: 2px dashed #000000;
              border-bottom: 2px dashed #000000;
            }
            .policies p {
              margin: 2px 0;
              text-align: center;
              color: #000000 !important;
              font-weight: bold;
            }
            .legal-notice {
              font-size: 10px;
              margin-top: 6px;
              text-align: center;
              padding: 4px 0;
            }
            .legal-notice p {
              margin: 2px 0;
              color: #000000 !important;
              font-weight: bold;
            }
            .contact {
              margin-top: 6px;
              font-size: 11px;
              text-align: center;
            }
            .contact p {
              color: #000000 !important;
            }
            @media print {
              body {
                padding: 0;
                margin: 0;
                font-size: 13px !important;
                color: #000000 !important;
                background: white !important;
              }
              @page {
                size: 80mm auto;
                margin: 0;
                padding: 0;
              }
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color: #000000 !important;
                background: white !important;
              }
              .header h1,
              .fiscal-notice,
              .info-line,
              .info-label,
              .item-row,
              .summary-line,
              .summary-total,
              .payment-line,
              .footer p,
              .policies p,
              .legal-notice p,
              .contact p,
              .via-label {
                color: #000000 !important;
                font-weight: bold;
              }
            }
          </style>
        </head>
        <body>
          ${gerarConteudoCupom(via === 'ambas' ? 'cliente' : via, finalOptions, printerType)}
          ${via === 'ambas' ? `<div class="separator">--- VIA ESTABELECIMENTO ---</div>${gerarConteudoCupom('estabelecimento', finalOptions, printerType)}` : ''}
        </body>
      </html>
    `;

    // Abrir nova janela com o cupom
    const newWindow = window.open("", "_blank");
    if (newWindow) {
      newWindow.document.write(cupomHtml);
      newWindow.document.close();

      // Aguardar carregamento e abrir diálogo de impressão
      newWindow.onload = () => {
        setTimeout(() => {
          newWindow.print();
        }, 250);
      };
    } else {
      toast.error("Não foi possível abrir a janela. Verifique se os pop-ups estão bloqueados.");
    }
  };

  const handleGeneratePickupReceipt = () => {
    if (!order) {
      toast.error("Pedido não encontrado");
      return;
    }

    const formatCurrency = (value: number) => {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value);
    };

    const customerName = order.client || order.customer_name || 'Cliente não identificado';
    const empresaNome = empresa?.nome || 'Ateliê Pro';
    const empresaLogo = empresa?.logo_url || '';
    const orderDiscount = (order as any)?.discount || 0;
    const orderDiscountType = (order as any)?.discount_type || 'fixed';

    // Calculate base total value
    let totalValue = Number(order.value) || 0;
    let discountAmount = orderDiscountType === 'percentage'
      ? (totalValue * orderDiscount / 100)
      : orderDiscount;
    let finalValue = Math.max(0, totalValue - discountAmount);

    // ✅ Gerar linhas da tabela a partir dos itens (personalizations, catalog_products, items ou description)
    // ✅ NOVO: Respeitar as opções selecionadas do modal
    const descriptionLines = (() => {
      const items = [];

      // Se não incluir itens, retornar vazio
      if (!receiptOptions.includeItems && !receiptOptions.includePersonalizations) {
        return `<tr><td colspan="3" style="text-align: center;">Sem itens especificados</td></tr>`;
      }

      // 1️⃣ Prioridade: personalizations (itens personalizados adicionados manualmente) - RESPEITAR OPÇÃO
      if (receiptOptions.includePersonalizations && order.personalizations && Array.isArray(order.personalizations) && order.personalizations.length > 0) {
        const totalQty = order.personalizations.reduce((sum: number, p: any) => sum + (p.quantity || 1), 0);
        const pricePerItem = order.value / Math.max(totalQty, 1); // Dividir valor total pela quantidade total

        order.personalizations.forEach((personalization: any, index: number) => {
          const personalizationKey = personalization.id || `personalization-${index}`;
          const itemName = `${personalization.person_name || 'Personalizado'}${personalization.size ? ` (Tam. ${personalization.size})` : ''}`;
          const totalQuantity = personalization.quantity || 1;
          const displayQuantity = receiptOptions.isPartialDelivery
            ? (receiptOptions.selectedItemsForDelivery[personalizationKey] || 0)
            : totalQuantity;

          // Se é entrega parcial E a quantidade selecionada é 0, pular esta personalização
          if (receiptOptions.isPartialDelivery && displayQuantity === 0) {
            return;
          }

          const itemTotal = (pricePerItem * displayQuantity).toFixed(2);
          const formattedValue = order.value > 0 ? `R$ ${itemTotal.replace('.', ',')}` : '—';
          const isPartial = receiptOptions.isPartialDelivery && displayQuantity < totalQuantity;

          items.push(`
            <tr>
              <td>${itemName}${isPartial ? ` <strong style="color: red;">[${displayQuantity}/${totalQuantity}]</strong>` : ''}</td>
              <td style="text-align: center;">${displayQuantity}</td>
              <td style="text-align: right;">${formattedValue}</td>
            </tr>
          `);
        });
      }

      // 2️⃣ catalog_products (produtos do catálogo) - RESPEITAR OPÇÃO
      if (receiptOptions.includeItems && order.catalog_products && Array.isArray(order.catalog_products) && order.catalog_products.length > 0) {
        order.catalog_products.forEach((product: any, index: number) => {
          const productKey = product.id || `item-${index}`;
          const description = product.description || product.name || 'Produto/Serviço';
          const totalQuantity = product.quantity || 1;
          const displayQuantity = receiptOptions.isPartialDelivery
            ? (receiptOptions.selectedItemsForDelivery[productKey] || 0)
            : totalQuantity;

          // Se é entrega parcial E a quantidade selecionada é 0, pular este item
          if (receiptOptions.isPartialDelivery && displayQuantity === 0) {
            return;
          }

          const unitValue = product.unit_value || (order.value / order.catalog_products.length);
          const formattedValue = unitValue > 0 ? `R$ ${unitValue.toFixed(2).replace('.', ',')}` : '—';
          const isPartial = receiptOptions.isPartialDelivery && displayQuantity < totalQuantity;

          items.push(`
            <tr>
              <td>${description}${isPartial ? ` <strong style="color: red;">[${displayQuantity}/${totalQuantity}]</strong>` : ''}</td>
              <td style="text-align: center;">${displayQuantity}</td>
              <td style="text-align: right;">${formattedValue}</td>
            </tr>
          `);
        });
      }

      // 3️⃣ order.items - RESPEITAR OPÇÃO
      if (items.length === 0 && receiptOptions.includeItems && order.items && order.items.length > 0) {
        order.items.forEach((item: any, index: number) => {
          const itemKey = item.id || `item-${index}`;
          const description = item.description || 'Produto/Serviço';
          const totalQuantity = item.quantity || 1;
          const displayQuantity = receiptOptions.isPartialDelivery
            ? (receiptOptions.selectedItemsForDelivery[itemKey] || 0)
            : totalQuantity;

          // Se é entrega parcial E a quantidade selecionada é 0, pular este item
          if (receiptOptions.isPartialDelivery && displayQuantity === 0) {
            return;
          }

          const unitValue = item.unit_value || 0;
          const formattedValue = unitValue > 0 ? `R$ ${unitValue.toFixed(2).replace('.', ',')}` : '—';
          const isPartial = receiptOptions.isPartialDelivery && displayQuantity < totalQuantity;

          items.push(`
            <tr>
              <td>${description}${isPartial ? ` <strong style="color: red;">[${displayQuantity}/${totalQuantity}]</strong>` : ''}</td>
              <td style="text-align: center;">${displayQuantity}</td>
              <td style="text-align: right;">${formattedValue}</td>
            </tr>
          `);
        });
      }

      // 4️⃣ description (parsing manual) - RESPEITAR OPÇÃO
      if (items.length === 0 && receiptOptions.includeItems && order.description) {
        order.description
          .split('\n')
          .filter((line: string) => line.trim() && !line.trim().toLowerCase().startsWith('total:'))
          .forEach((line: string) => {
            const qtyMatch = line.match(/Qtd[^:]*:\s*(\d+(?:[.,]\d+)?)/i) || line.match(/quantidade[^:]*:\s*(\d+(?:[.,]\d+)?)/i);
            const qty = qtyMatch ? qtyMatch[1].replace(',', '.') : '1';

            const valueMatch = line.match(/R\$\s*([\d.,]+)/);
            let value = '—';
            if (valueMatch) {
              value = `R$ ${valueMatch[1]}`;
            }

            let itemName = line;
            const pipeIndex = line.indexOf('|');
            if (pipeIndex > -1) {
              itemName = line.substring(0, pipeIndex).trim();
            } else {
              itemName = line.replace(/\s*\|.*$/i, '').replace(/Qtd[^:]*:\s*[\d.,]+/i, '').replace(/R\$\s*[\d.,]+/i, '').trim();
            }

            items.push(`
              <tr>
                <td>${itemName}</td>
                <td style="text-align: center;">${qty}</td>
                <td style="text-align: right;">${value}</td>
              </tr>
            `);
          });
      }

      return items.join('') || `<tr><td colspan="3" style="text-align: center;">Sem itens especificados</td></tr>`;
    })();

    // 🔧 Recalcular valores se for entrega parcial (baseado no valor REAL de cada item selecionado)
    if (receiptOptions.isPartialDelivery) {
      let selectedItemsTotal = 0;

      // Somar valor dos catalog_products selecionados
      if (order?.catalog_products?.length > 0) {
        order.catalog_products.forEach((product: any, index: number) => {
          const productKey = product.id || `item-${index}`;
          const selectedQty = receiptOptions.selectedItemsForDelivery[productKey] || 0;
          if (selectedQty > 0) {
            const itemValue = (product.unit_value || 0) * selectedQty;
            selectedItemsTotal += itemValue;
          }
        });
      }

      // Somar valor das personalizations selecionadas (distribuir valor proporcional)
      if (selectedItemsTotal === 0 && order?.personalizations?.length > 0) {
        const totalQtyPersonalizations = order.personalizations.reduce((sum: number, p: any) => sum + (p.quantity || 1), 0);
        const pricePerItem = totalValue / Math.max(totalQtyPersonalizations, 1);

        order.personalizations.forEach((personalization: any, index: number) => {
          const personalizationKey = personalization.id || `personalization-${index}`;
          const selectedQty = receiptOptions.selectedItemsForDelivery[personalizationKey] || 0;
          if (selectedQty > 0) {
            selectedItemsTotal += pricePerItem * selectedQty;
          }
        });
      }

      // Somar valor dos items selecionados
      if (selectedItemsTotal === 0 && order?.items?.length > 0) {
        order.items.forEach((item: any, index: number) => {
          const itemKey = item.id || `item-${index}`;
          const selectedQty = receiptOptions.selectedItemsForDelivery[itemKey] || 0;
          if (selectedQty > 0) {
            const itemValue = (item.unit_value || 0) * selectedQty;
            selectedItemsTotal += itemValue;
          }
        });
      }

      // Se houver items selecionados, usar o valor calculado
      if (selectedItemsTotal > 0) {
        totalValue = selectedItemsTotal;
        discountAmount = orderDiscountType === 'percentage'
          ? (totalValue * orderDiscount / 100)
          : (orderDiscount * (selectedItemsTotal / Math.max(Number(order.value), 1))); // Proporcionar desconto fixo
        finalValue = Math.max(0, totalValue - discountAmount);
      }
    }

    const receiptHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Recibo de Retirada - ${order.code}</title>
          <meta charset="utf-8">
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Arial', sans-serif; padding: 30px; color: #333; }
            .container { max-width: 600px; margin: 0 auto; border: 2px solid #000; padding: 30px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #000 !important; padding: 20px !important; background: #f0f0f0 !important; border-radius: 6px !important; }
            .logo-section { display: flex; align-items: center; justify-content: center; margin-bottom: 15px; }
            .logo-section img {
              max-width: 100px;
              max-height: 80px;
              object-fit: contain;
              border: 3px solid #333;
              border-radius: 8px;
              padding: 8px;
              background: #fff;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            }
            .company-info { text-align: center; }
            .header h1 { font-size: 20px; font-weight: bold; margin-bottom: 5px; }
            .header .company-name { font-size: 14px; font-weight: bold; margin-bottom: 8px; }
            .header p { font-size: 12px; color: #666; }
            .info-section { margin-bottom: 20px; }
            .info-row { display: grid; grid-template-columns: 150px 1fr; margin-bottom: 8px; font-size: 14px; }
            .info-label { font-weight: bold; }
            .items-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            .items-table th, .items-table td { border: 1px solid #000; padding: 8px; text-align: left; font-size: 12px; }
            .items-table th { background: #f0f0f0; font-weight: bold; }
            .legal-section { margin: 30px 0; padding: 15px; border: 1px solid #999; background: #fafafa; font-size: 11px; line-height: 1.6; }
            .legal-section p { margin: 10px 0; }
            .signature-section { margin-top: 40px; }
            .signature-line { margin-top: 50px; border-top: 1px solid #000; padding-top: 5px; font-size: 12px; text-align: center; }
            .two-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; }
            .footer { text-align: center; margin-top: 20px; font-size: 10px; color: #666; }
            @media print {
              body { padding: 0; }
              .container { border: none; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              ${empresaLogo ? `
              <div class="logo-section">
                <img src="${empresaLogo}" alt="Logo da empresa" />
              </div>
              ` : ''}
              <div class="company-info">
                <div class="company-name">${empresaNome}</div>
                <h1>RECIBO DE RETIRADA</h1>
              </div>
            </div>

            <div class="info-section">
              <div class="info-row">
                <div class="info-label">Pedido Nº:</div>
                <div>${order.code}</div>
              </div>
              <div class="info-row">
                <div class="info-label">Cliente:</div>
                <div>${customerName}</div>
              </div>
              ${receiptOptions.includeDeliveryDate ? `
              <div class="info-row">
                <div class="info-label">Data de Retirada:</div>
                <div>${(() => {
                  const [year, month, day] = receiptOptions.deliveryDate.split('-');
                  return `${day}/${month}/${year}`;
                })()}</div>
              </div>
              ` : `
              <div class="info-row">
                <div class="info-label">Data de Retirada:</div>
                <div>____ / ____ / ________</div>
              </div>
              `}
              ${receiptOptions.isPartialDelivery ? `
              <div class="info-row" style="color: #dc2626; font-weight: bold; background: #fee2e2; padding: 8px; border-radius: 4px;">
                <div class="info-label">⚠️ Status:</div>
                <div>ENTREGA PARCIAL</div>
              </div>
              ` : ''}
            </div>

            <div class="info-section">
              <p style="font-weight: bold; margin-bottom: 10px;">Itens do Pedido:</p>
              <table class="items-table">
                <thead>
                  <tr>
                    <th style="width: 60%;">Descrição</th>
                    <th style="width: 20%;">Quantidade</th>
                    <th style="width: 20%;">Valor (R$)</th>
                  </tr>
                </thead>
                <tbody>
                  ${descriptionLines}
                </tbody>
              </table>
              ${orderDiscount > 0 ? `
              <div style="margin-top: 15px; padding: 10px; background: #f3f4f6; border: 1px solid #e5e7eb; border-radius: 4px;">
                <div style="display: grid; grid-template-columns: 1fr auto; gap: 15px; margin-bottom: 10px;">
                  <div style="text-align: right;">
                    <div style="font-size: 12px; color: #6b7280; margin-bottom: 5px;">Subtotal</div>
                    <div style="font-size: 12px; color: #6b7280; margin-bottom: 5px;">Desconto ${orderDiscountType === 'percentage' ? `(${orderDiscount.toFixed(2)}%)` : '(Fixo)'}</div>
                    <div style="font-size: 14px; font-weight: bold; color: #1f2937; border-top: 1px solid #d1d5db; padding-top: 8px;">Valor Final</div>
                  </div>
                  <div style="margin-top: 10px; background: #f5f5f5; border: 2px solid #333; border-radius: 6px; padding: 10px; font-size: 12px;">
                    <div style="display: flex; justify-content: space-between; margin-bottom: 6px; padding-bottom: 6px; border-bottom: 1px solid #ddd;">
                      <span>Valor Original:</span>
                      <span style="font-weight: bold;">${formatCurrency(totalValue)}</span>
                    </div>
                    ${orderDiscount > 0 ? `
                    <div style="display: flex; justify-content: space-between; margin-bottom: 6px; padding-bottom: 6px; border-bottom: 1px solid #ddd;">
                      <span>Desconto:</span>
                      <span style="font-weight: bold;">-${formatCurrency(discountAmount)}</span>
                    </div>
                    ` : ''}
                    <div style="display: flex; justify-content: space-between; margin-bottom: 6px; padding-bottom: 6px; border-bottom: 2px solid #333; font-weight: bold;">
                      <span>Valor a Pagar:</span>
                      <span>${formatCurrency(finalValue)}</span>
                    </div>
                    ${order.paid > 0 ? `
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span>Valor Recebido:</span>
                      <span style="font-weight: bold;">${formatCurrency(order.paid)}</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; font-weight: bold;">
                      <span>Saldo Devedor:</span>
                      <span>${formatCurrency(Math.max(0, finalValue - order.paid))}</span>
                    </div>
                    ` : ''}
                  </div>
                </div>
              </div>
              ` : `
              <div style="margin-top: 15px; background: #f5f5f5; border: 2px solid #333; border-radius: 6px; padding: 12px; font-size: 13px;">
                <div style="display: flex; justify-content: space-between; margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid #ddd;">
                  <span>Valor Original:</span>
                  <span style="font-weight: bold;">${formatCurrency(totalValue)}</span>
                </div>
                ${orderDiscount > 0 ? `
                <div style="display: flex; justify-content: space-between; margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid #ddd;">
                  <span>Desconto:</span>
                  <span style="font-weight: bold;">-${formatCurrency(discountAmount)}</span>
                </div>
                ` : ''}
                <div style="display: flex; justify-content: space-between; margin-bottom: 8px; padding-bottom: 8px; border-bottom: 2px solid #333; font-size: 14px; font-weight: bold;">
                  <span>Valor a Pagar:</span>
                  <span>${formatCurrency(finalValue)}</span>
                </div>
                ${order.paid > 0 ? `
                <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
                  <span>Valor Recebido:</span>
                  <span style="font-weight: bold;">${formatCurrency(order.paid)}</span>
                </div>
                <div style="display: flex; justify-content: space-between; font-size: 14px; font-weight: bold;">
                  <span>Saldo Devedor:</span>
                  <span>${formatCurrency(Math.max(0, finalValue - order.paid))}</span>
                </div>
                ` : ''}
              </div>
              `}
            </div>

            ${receiptOptions.includeDescription && order.description ? `
            <div class="info-section" style="margin-top: 15px; padding: 10px; background: #f9f9f9; border: 1px solid #e0e0e0; border-radius: 4px;">
              <p style="font-weight: bold; margin-bottom: 8px;">📝 Descrição Completa:</p>
              <p style="font-size: 13px; line-height: 1.5; white-space: pre-wrap; word-wrap: break-word;">${order.description}</p>
            </div>
            ` : ''}

            <div class="legal-section">
              <p><strong>DECLARAÇÃO:</strong></p>
              <p>Declaro que conferi e recebi os produtos/serviços deste pedido em perfeito estado, sem defeitos ou danos.</p>
            </div>

            <div class="signature-section">
              <div class="two-cols">
                <div class="signature-line">
                  Assinatura do Cliente
                </div>
                <div class="signature-line">
                  Data: ____ / ____ / ______
                </div>
              </div>
            </div>

            <div class="footer">
              <p>Gerado em ${new Date().toLocaleString('pt-BR')} - ${empresaNome}</p>
            </div>
          </div>
        </body>
      </html>
    `;

    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(receiptHtml);
      newWindow.document.close();
      newWindow.onload = () => {
        newWindow.print();
      };
    }
  };

  const steps = useMemo(() => {
    // 🔧 NOVO: Usar productionTimeline.steps se disponível (sistema unificado)
    if (productionTimeline?.steps && productionTimeline.steps.length > 0) {
      return productionTimeline.steps.map(step => ({
        key: step.key as OrderItem["status"],
        label: step.label,
        description: step.description || "",
        icon: Clock, // Fallback, pode ser customizado depois se necessário
      }));
    }

    // FALLBACK: Sistema antigo (para compatibilidade com pedidos muito antigos)
    const defaultMap = new Map(DEFAULT_ORDER_STATUS_DETAILS.map((detail) => [detail.key, detail]));
    const baseSteps: Array<{ key: OrderItem["status"]; icon: any }> = [
      { key: ORDER_STATUS.AGUARDANDO_APROVACAO as OrderItem["status"], icon: Clock },
      { key: ORDER_STATUS.EM_PRODUCAO as OrderItem["status"], icon: Play },
      { key: ORDER_STATUS.FINALIZANDO as OrderItem["status"], icon: Package },
      { key: ORDER_STATUS.PRONTO as OrderItem["status"], icon: CheckCircle },
      { key: ORDER_STATUS.AGUARDANDO_RETIRADA as OrderItem["status"], icon: Truck },
      { key: ORDER_STATUS.ENTREGUE as OrderItem["status"], icon: CheckCircle2 },
    ];

    return baseSteps.map((step) => {
      const detail = statusDetailsMap.get(step.key as any);
      const fallback = defaultMap.get(step.key as any);
      return {
        key: step.key,
        icon: step.icon,
        label: detail?.label ?? fallback?.label ?? step.key,
        description: detail?.description ?? fallback?.description ?? "",
      };
    });
  }, [productionTimeline?.steps, statusDetailsMap]);

  if (!order) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="flex items-center gap-4 p-4">
            <SidebarTrigger />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/pedidos")}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Pedido não encontrado</h1>
              <p className="text-sm text-muted-foreground">Verifique o código e tente novamente</p>
            </div>
          </div>
        </header>
      </div>
    );
  }

  // Se orderDb não está carregado, mostrar loading
  if (!orderDb || isLoading) {
    return <div className="min-h-screen bg-background p-4">Carregando pedido...</div>;
  }

  if (!order) {
    return <div className="min-h-screen bg-background p-4">Pedido não encontrado...</div>;
  }

  const currentStep = getStatusStepIndex(order.status);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="flex items-center gap-4 p-4">
          <SidebarTrigger />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/pedidos")}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">{order.code}</h1>
            <p className="text-sm text-muted-foreground">Detalhes do pedido</p>
          </div>
        </div>
      </header>

      {/* Dialog: Customização de Timeline Requerida */}
      <Dialog open={showCustomizationRequiredDialog} onOpenChange={setShowCustomizationRequiredDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              Timeline Personalizada Não Configurada
            </DialogTitle>
            <DialogDescription className="pt-2">
              Você selecionou o modo "Personalizado", mas ainda não configurou nenhuma etapa customizada.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Configure suas etapas personalizadas em <span className="font-semibold">Configurações → Timeline de Produção</span> e depois poderá usá-las aqui.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCustomizationRequiredDialog(false)}>
              Fechar
            </Button>
            <Button onClick={() => {
              setShowCustomizationRequiredDialog(false);
            }} className="gap-2">
              <Settings className="w-4 h-4" />
              Ir para Configurações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="p-6 max-w-5xl mx-auto space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
              <CardTitle className="text-foreground flex items-center gap-2">
                <Package className="w-5 h-5 text-primary" />
                Informações do Pedido
              </CardTitle>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/pedidos/editar/${order.id}`)}
                    className="flex items-center gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    Editar Pedido
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('acoes-section')?.scrollIntoView({ behavior: 'smooth' })}
                    className="flex items-center gap-2"
                  >
                    <Zap className="w-4 h-4" />
                    Ações
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Cliente</p>
                    <button
                      onClick={() => {
                        const customerName = order.client;
                        if (customerName) {
                          navigate(`/clientes?autoOpenClient=${encodeURIComponent(customerName)}`);
                        }
                      }}
                      className="text-sm font-medium text-blue-600 hover:text-blue-700 cursor-pointer transition-colors underline"
                      title="Clique para ver detalhes do cliente"
                    >
                      {order.client}
                      {telefoneCliente
                        ? <span className="ml-2 text-xs text-muted-foreground">({telefoneCliente})</span>
                        : null}
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Entrega</p>
                    <p className="text-sm font-medium text-foreground">
                      {order.delivery ? (() => {
                        // Parse a data como local para evitar problemas de timezone
                        const [year, month, day] = order.delivery.split('T')[0].split('-');
                        return `${day}/${month}/${year}`;
                      })() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Tipo</p>
                  <p className="text-sm font-medium text-foreground">{order.type}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Valor / Pago</p>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground">R$ {order.value} / R$ {order.paid}</p>
                    <Dialog open={isPaidDialogOpen} onOpenChange={setIsPaidDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                          <Edit className="w-3 h-3" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Editar Valor Pago</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="paid">Valor Pago (R$)</Label>
                            <Input
                              id="paid"
                              type="number"
                              step="0.01"
                              placeholder="0,00"
                              value={newPaidValue}
                              onChange={(e) => setNewPaidValue(e.target.value)}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={handleUpdatePaid} className="flex-1">
                              Atualizar
                            </Button>
                            <Button variant="outline" onClick={() => setIsPaidDialogOpen(false)}>
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status de Pagamento</p>
                  <div className="flex items-center gap-2 mt-1">
                    <CreditCard className="w-4 h-4 text-muted-foreground" />
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${paymentStatus.color}`}>
                      {paymentStatus.label}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border border-secondary/30 bg-secondary/20 text-secondary">
                    {order.status}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground">Forma de Pagamento</p>
                    <p className="text-sm font-medium text-foreground">
                      {(order as any).payment_method || "Não informada"}
                    </p>
                  </div>
                  <Dialog open={isPaymentMethodDialogOpen} onOpenChange={setIsPaymentMethodDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 mt-4">
                        <Edit className="w-3 h-3" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Editar Forma de Pagamento</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="payment-method">Forma de Pagamento</Label>
                          <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                            <SelectTrigger id="payment-method">
                              <SelectValue placeholder="Selecione uma forma de pagamento" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Sem forma de pagamento</SelectItem>
                              <SelectItem value="A combinar">⚪ A combinar</SelectItem>
                              <SelectItem value="Pix">📱 Pix</SelectItem>
                              <SelectItem value="Dinheiro">💵 Dinheiro</SelectItem>
                              <SelectItem value="Cartão de Débito">🏦 Cartão de Débito</SelectItem>
                              <SelectItem value="Cartão de Crédito">💳 Cartão de Crédito</SelectItem>
                              <SelectItem value="Transferência">🏧 Transferência</SelectItem>
                              <SelectItem value="Boleto">📋 Boleto</SelectItem>
                              {formasPagamento.length > 0 && (
                                <>
                                  <hr className="my-2" />
                                  {formasPagamento.map((forma) => (
                                    <SelectItem key={forma.id} value={forma.id}>
                                      {forma.nome}
                                    </SelectItem>
                                  ))}
                                </>
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={handleUpdatePaymentMethod} className="flex-1">
                            Atualizar
                          </Button>
                          <Button variant="outline" onClick={() => setIsPaymentMethodDialogOpen(false)}>
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              {((order as any)?.discount ?? 0) > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 mt-4 border-t border-border/50">
                  <div>
                    <p className="text-xs text-muted-foreground">Tipo de Desconto</p>
                    <p className="text-sm font-medium text-foreground capitalize">
                      {(order as any)?.discount_type === 'percentage' ? 'Percentual (%)' : 'Valor Fixo (R$)'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Valor do Desconto</p>
                    <p className="text-sm font-medium text-foreground">
                      {(order as any)?.discount_type === 'percentage'
                        ? `${((order as any)?.discount || 0)?.toFixed(2)}%`
                        : `R$ ${((order as any)?.discount || 0)?.toFixed(2)}`
                      }
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Valor com Desconto</p>
                    <p className="text-sm font-medium text-green-600 font-semibold">
                      R$ {(
                        (order as any)?.discount_type === 'percentage'
                          ? (order.value * (1 - ((order as any)?.discount || 0) / 100))
                          : Math.max(0, order.value - ((order as any)?.discount || 0))
                      )?.toFixed(2)}
                    </p>
                  </div>
                </div>
              )}

              {/* Lista de Itens do Pedido */}
              {(() => {
                // Verificar se deve separar em linhas (marcador no campo observations)
                const shouldSeparateLines = (order as any).observations?.includes("__SEPARATE_LINES__") ?? false;
                
                // Função para parsear itens da descrição
                const parseOrderItems = (description: string) => {
                  if (!description) return [];

                  const hasStructuredMarkers = /Kit:|Qtd:|Tamanho:|Cor:/i.test(description) || description.includes('\n');

                  // Se não deve separar e não há marcadores estruturados, retornar descrição como único item
                  if (!shouldSeparateLines && !hasStructuredMarkers) {
                    return [{
                      name: description.trim(),
                      quantity: 1,
                      fullText: description.trim()
                    }];
                  }
                  
                  const lines = description.split('\n').filter(line => line.trim());
                  const items: Array<{
                    name: string;
                    quantity: number;
                    size?: string;
                    color?: string;
                    fullText: string;
                  }> = [];

                  const baseName = lines[0]?.split('|')[0].trim() || "Item";
                  
                  lines.forEach(line => {
                    if (line.trim().startsWith('Total:')) {
                      return;
                    }

                    if (line.trim().startsWith('Kit:')) {
                      const kitInfo = line.replace(/^Kit:\s*/i, '').trim();
                      kitInfo
                        .split('|')
                        .map(part => part.trim())
                        .forEach((part) => {
                          const match = part.match(/(\d+)\s+(.+)/i);
                          if (!match) return;
                          items.push({
                            name: baseName,
                            quantity: parseInt(match[1], 10),
                            size: match[2].trim(),
                            fullText: line.trim(),
                          });
                        });
                      return;
                    }

                    // Tentar extrair informações estruturadas
                    const qtdMatch = line.match(/Qtd:\s*(\d+)/i);
                    const sizeMatch = line.match(/Tamanho:\s*([^|]+)/i);
                    const colorMatch = line.match(/Cor:\s*([^|]+)/i);
                    // Tentar encontrar preço em vários formatos: "R$ XXX", "R$ XXX,XX", "Valor: XXX", etc
                    const priceMatch = line.match(/R\$\s*([\d.,]+)/) ||
                                      line.match(/Valor[:\s]+\s*([\d.,]+)/i) ||
                                      line.match(/V:\s*([\d.,]+)/i);

                    // Extrair nome do produto (tudo antes do primeiro "|")
                    const namePart = line.split('|')[0].trim();
                    // Remover informações entre parênteses que são duplicadas
                    const cleanName = namePart
                      .replace(/\s*-\s*[^(]+\([^)]+\)/g, '') // Remove "- Tipo (materiais)"
                      .replace(/\s*\([^)]+\)/g, '') // Remove parênteses restantes
                      .trim();

                    const quantity = qtdMatch ? parseInt(qtdMatch[1]) : 1;
                    const size = sizeMatch ? sizeMatch[1].trim() : undefined;
                    const color = colorMatch ? colorMatch[1].trim() : undefined;
                    const price = priceMatch ? priceMatch[1] : undefined;
                    
                    // Se não encontrou estrutura, usar a linha inteira como nome
                    if (!qtdMatch && !sizeMatch && !colorMatch) {
                      items.push({
                        name: line.trim(),
                        quantity: 1,
                        price: price,
                        fullText: line.trim()
                      });
                    } else {
                      items.push({
                        name: cleanName || namePart,
                        quantity,
                        size: size && size !== "Não aplicável" ? size : undefined,
                        color: color || undefined,
                        price: price,
                        fullText: line.trim()
                      });
                    }
                  });
                  
                  return items;
                };
                
                // Usar itens reais do banco se existirem, senão fazer fallback para parsing da descrição
                const orderItems = (order.items && order.items.length > 0)
                  ? order.items.map((item: any) => ({
                      name: item.description || 'Produto/Serviço',
                      quantity: item.quantity || 1,
                      price: item.unit_value ? item.unit_value.toFixed(2) : undefined,
                      fullText: item.description || '',
                      category: item.category || '',
                      especificacoes: item.especificacoes || {}
                    }))
                  : parseOrderItems(order.description);

                if (orderItems.length > 0) {
                  return (
                    <div className="space-y-3 border-b border-dashed pb-4 mb-4">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Itens do Pedido
                        </p>
                        <span className="text-xs text-muted-foreground">
                          {orderItems.length} {orderItems.length === 1 ? 'item' : 'itens'}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {orderItems.map((item, index) => (
                          <div
                            key={index}
                            className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg border border-border/50"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium text-foreground">{item.name}</p>
                                {item.price && (
                                  <p className="text-sm font-semibold text-primary ml-2">R$ {item.price}</p>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-3 mt-1">
                                <span className="text-xs text-muted-foreground">
                                  <strong>Qtd:</strong> {item.quantity}
                                </span>
                                {item.size && (
                                  <span className="text-xs text-muted-foreground">
                                    <strong>Tamanho:</strong> {item.size}
                                  </span>
                                )}
                                {item.color && (
                                  <span className="text-xs text-muted-foreground">
                                    <strong>Cor:</strong> {item.color}
                                  </span>
                                )}
                              </div>
                              {/* Renderizar especificações se existirem */}
                              {item.especificacoes && typeof item.especificacoes === 'object' && Object.keys(item.especificacoes).length > 0 && (
                                <div className="mt-3 pt-3 border-t border-border/50">
                                  <p className="text-xs font-semibold text-muted-foreground mb-2">Especificações:</p>
                                  <div className="space-y-1">
                                    {Object.entries(item.especificacoes).filter(([, value]) => value !== null && value !== undefined && value !== '').map(([key, value]) => (
                                      <div key={key} className="text-xs text-muted-foreground">
                                        <strong>{formatFieldName(key)}:</strong> {String(value)}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              <div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">Descrição Completa</p>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                        <Edit className="w-3 h-3" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Editar Descrição do Pedido</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="description">Descrição</Label>
                          <Textarea
                            id="description"
                            placeholder="Descrição do pedido"
                            defaultValue={order.description}
                            onChange={(e) => setNewDescription(e.target.value)}
                            rows={6}
                            className="resize-none"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={handleUpdateDescription} className="flex-1">
                            Atualizar
                          </Button>
                          <Button variant="outline" onClick={() => setNewDescription("")}>
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <p className="text-foreground mt-1 whitespace-pre-wrap text-sm">{order.description}</p>
              </div>

              {order.personalizations && order.personalizations.length > 0 && (
                <div className="space-y-2 border-t border-dashed pt-4 mt-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Personalizações
                    </p>
                    <span className="text-xs text-muted-foreground">
                      {order.personalizations.length} peça{order.personalizations.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {order.personalizations.map((item, index) => {
                      // Criar uma chave única: usar ID se disponível, senão usar índice + campos únicos
                      const uniqueKey = item.id || `${index}-${item.person_name}-${item.size}-${item.quantity}-${item.notes}`;
                      return (
                      <div
                          key={uniqueKey}
                        className="flex flex-col gap-1 rounded-md border border-border/80 bg-muted/20 p-3 text-xs sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-foreground">{item.person_name}</span>
                          <span className="text-muted-foreground">
                            {item.size ? `Tamanho: ${item.size}` : "Tamanho não informado"}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-muted-foreground">
                          <span>Qtd: {item.quantity}</span>
                          {item.notes && (
                            <span className="max-w-[240px] truncate text-muted-foreground/90">
                              {item.notes}
                            </span>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {id && (
                <ArquivosSection
                  tableType="orders"
                  recordId={id}
                  arquivos={arquivos}
                  onArquivosUpdated={setArquivos}
                  isExpanded={arquivos.length > 0}
                />
              )}

              {/* Seção de Conserto (FASE 1) */}
              <ConsertoPedidoCard
                consertoDados={(orderDb as any)?.conserto_dados}
                tipoPedido={order?.type || ""}
              />

              {/* Seção de Prova Agendada (FASE 3) */}
              <ProvaPedidoCard
                provaAgendada={provaAgendada || undefined}
              />

              {/* Seção de Medidas do Cliente */}
              {medidas.filter(medida => 
                medida.cliente_nome.toLowerCase().includes(order.client.toLowerCase())
              ).length > 0 && (
                <div className="space-y-3 border-t pt-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    <p className="text-sm font-medium text-foreground">Medidas do Cliente</p>
                  </div>
                  <div className="space-y-3">
                    {medidas
                      .filter(medida => 
                        medida.cliente_nome.toLowerCase().includes(order.client.toLowerCase())
                      )
                      .map((medida) => (
                        <div key={medida.id} className="p-3 bg-muted/50 rounded-lg border">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-semibold text-primary">
                              {medida.tipo_peca.toUpperCase()}
                            </h4>
                            <span className="text-xs text-muted-foreground">
                              {new Date(medida.created_at).toLocaleDateString('pt-BR')}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                            {medida.busto && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Busto:</span>
                                <span className="font-medium">{medida.busto}cm</span>
                              </div>
                            )}
                            {medida.cintura && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Cintura:</span>
                                <span className="font-medium">{medida.cintura}cm</span>
                              </div>
                            )}
                            {medida.quadril && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Quadril:</span>
                                <span className="font-medium">{medida.quadril}cm</span>
                              </div>
                            )}
                            {medida.ombro && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Ombro:</span>
                                <span className="font-medium">{medida.ombro}cm</span>
                              </div>
                            )}
                            {medida.largura_costas && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Larg. Costas:</span>
                                <span className="font-medium">{medida.largura_costas}cm</span>
                              </div>
                            )}
                            {medida.cava_manga && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Cava Manga:</span>
                                <span className="font-medium">{medida.cava_manga}cm</span>
                              </div>
                            )}
                            {medida.grossura_braco && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Gross. Braço:</span>
                                <span className="font-medium">{medida.grossura_braco}cm</span>
                              </div>
                            )}
                            {medida.comprimento_manga && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Comp. Manga:</span>
                                <span className="font-medium">{medida.comprimento_manga}cm</span>
                              </div>
                            )}
                            {medida.cana_braco && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Cana Braço:</span>
                                <span className="font-medium">{medida.cana_braco}cm</span>
                              </div>
                            )}
                            {medida.alca && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Alça:</span>
                                <span className="font-medium">{medida.alca}cm</span>
                              </div>
                            )}
                            {medida.pescoco && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Pescoço:</span>
                                <span className="font-medium">{medida.pescoco}cm</span>
                              </div>
                            )}
                            {medida.comprimento && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Comprimento:</span>
                                <span className="font-medium">{medida.comprimento}cm</span>
                              </div>
                            )}
                            {medida.coxa && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Coxa:</span>
                                <span className="font-medium">{medida.coxa}cm</span>
                              </div>
                            )}
                            {medida.tornozelo && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Tornozelo:</span>
                                <span className="font-medium">{medida.tornozelo}cm</span>
                              </div>
                            )}
                            {medida.comprimento_calca && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Comp. Calça:</span>
                                <span className="font-medium">{medida.comprimento_calca}cm</span>
                              </div>
                            )}
                          </div>
                          
                          {(medida.detalhes_superior || medida.detalhes_inferior || medida.observacoes) && (
                            <div className="mt-3 space-y-2">
                              {medida.detalhes_superior && (
                                <div className="p-2 bg-blue-50 rounded text-xs">
                                  <strong className="text-blue-800">Detalhes Superiores:</strong>
                                  <span className="text-blue-700 ml-1">{medida.detalhes_superior}</span>
                                </div>
                              )}
                              {medida.detalhes_inferior && (
                                <div className="p-2 bg-blue-50 rounded text-xs">
                                  <strong className="text-blue-800">Detalhes Inferiores:</strong>
                                  <span className="text-blue-700 ml-1">{medida.detalhes_inferior}</span>
                                </div>
                              )}
                              {medida.observacoes && (
                                <div className="p-2 bg-yellow-50 rounded text-xs">
                                  <strong className="text-yellow-800">Observações:</strong>
                                  <span className="text-yellow-700 ml-1">{medida.observacoes}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Coluna da direita: Timeline + Recebimentos */}
          <div className="flex flex-col gap-6">
            {/* Timeline de Produção */}
            <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100/50 shadow-lg overflow-hidden">
            <CardHeader className="pb-3 border-b border-purple-200/60 px-4 py-3 flex flex-row items-center justify-between space-y-0">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-purple-200/50">
                  <Clock className="w-4 h-4 text-purple-700" />
                </div>
                <CardTitle className="text-purple-900 text-base m-0">
                  Timeline de Produção
                </CardTitle>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 rounded-lg border border-purple-300/50 hover:border-purple-400 hover:bg-purple-100/50 text-purple-600"
                    onClick={handleOpenCustomizeStatuses}
                  >
                    <Edit className="h-4 w-4" />
                    <span className="sr-only">Personalizar etapas</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left" align="end">
                </TooltipContent>
              </Tooltip>
            </CardHeader>
            <Dialog
              open={isCustomizeDialogOpen}
              onOpenChange={(open) => {
                setIsCustomizeDialogOpen(open);
                if (open) {
                  setStatusDraft(statusDetails.map((detail) => ({ ...detail })));
                }
              }}
            >
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Personalizar etapas da produção</DialogTitle>
                  <DialogDescription>
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                  {statusDraft.map((status, index) => {
                    const baseId = status.key
                      .normalize("NFD")
                      .replace(/[\u0300-\u036f]/g, "")
                      .replace(/[^a-zA-Z0-9]+/g, "-")
                      .toLowerCase();

                    return (
                      <div
                        key={status.key}
                        draggable
                        onDragStart={() => setDraggedStatusKey(status.key)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => {
                          if (!draggedStatusKey || draggedStatusKey === status.key) return;

                          const draggedIndex = statusDraft.findIndex(s => s.key === draggedStatusKey);
                          const targetIndex = index;

                          if (draggedIndex !== -1) {
                            const newStatusDraft = [...statusDraft];
                            const [draggedItem] = newStatusDraft.splice(draggedIndex, 1);
                            newStatusDraft.splice(targetIndex, 0, draggedItem);
                            setStatusDraft(newStatusDraft);
                            setDraggedStatusKey(null);
                          }
                        }}
                        className={`rounded-lg border border-border/60 bg-muted/20 p-4 space-y-3 cursor-move transition ${
                          draggedStatusKey === status.key ? 'opacity-50' : ''
                        }`}
                      >
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Etapa original</p>
                          <p className="text-sm font-semibold text-foreground">{status.defaultLabel}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleStatusDraftRestore(status.key)}
                          disabled={
                            status.label === status.defaultLabel &&
                            status.description === status.defaultDescription
                          }
                        >
                          Restaurar padrão
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`status-label-${baseId}`}>Nome exibido</Label>
                        <Input
                          id={`status-label-${baseId}`}
                          value={status.label}
                          onChange={(event) =>
                            handleStatusDraftChange(status.key, "label", event.target.value)
                          }
                          maxLength={60}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`status-description-${baseId}`}>Descrição (opcional)</Label>
                        <Textarea
                          id={`status-description-${baseId}`}
                          value={status.description}
                          onChange={(event) =>
                            handleStatusDraftChange(status.key, "description", event.target.value)
                          }
                          maxLength={160}
                          placeholder={status.defaultDescription}
                        />
                      </div>
                    </div>
                    );
                  })}

                  {/* Etapas de Produção Customizadas */}
                  {productionTimeline?.steps && productionTimeline.steps.filter(s => s.key.startsWith("CUSTOM_")).length > 0 && (
                    <>
                      <div className="border-t border-border/60 my-4 pt-4">
                        <h3 className="text-sm font-semibold text-foreground mb-3">Etapas Customizadas</h3>
                      </div>
                      {productionTimeline.steps
                        .filter(s => s.key.startsWith("CUSTOM_"))
                        .map((step) => {
                          const customBaseId = step.key.toLowerCase();

                          return (
                            <div
                              key={step.key}
                              draggable
                              onDragStart={() => setDraggedStatusKey(step.key)}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => {
                                if (!draggedStatusKey || draggedStatusKey === step.key) return;

                                const draggedStep = productionTimeline.steps.find(s => s.key === draggedStatusKey);
                                if (!draggedStep || !draggedStep.key.startsWith("CUSTOM_")) return;

                                const updatedSteps = productionTimeline.steps.map((s) => s);
                                const draggedIndex = updatedSteps.findIndex(s => s.key === draggedStatusKey);
                                const targetIndex = updatedSteps.findIndex(s => s.key === step.key);

                                if (draggedIndex !== -1) {
                                  const [draggedItem] = updatedSteps.splice(draggedIndex, 1);
                                  updatedSteps.splice(targetIndex, 0, draggedItem);
                                  setProductionTimeline({ ...productionTimeline, steps: updatedSteps });
                                  setDraggedStatusKey(null);
                                }
                              }}
                              className={`rounded-lg border border-purple-200 bg-purple-50/30 p-4 space-y-3 cursor-move transition ${
                                draggedStatusKey === step.key ? 'opacity-50' : ''
                              }`}
                            >
                              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                  <p className="text-xs font-medium uppercase tracking-wide text-purple-600">Etapa Customizada</p>
                                  <p className="text-sm font-semibold text-foreground">{step.label}</p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={async () => {
                                    if (orderDb?.id) {
                                      const result = await removeProductionStep(orderDb.id, step.key);
                                      if (result.ok) {
                                        const updatedSteps = productionTimeline.steps.filter((s) => s.key !== step.key);
                                        setProductionTimeline({ ...productionTimeline, steps: updatedSteps });
                                        toast.success("Etapa removida!");
                                      } else {
                                        toast.error(result.error || "Erro ao remover etapa");
                                      }
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor={`custom-label-${customBaseId}`}>Nome</Label>
                                <Input
                                  id={`custom-label-${customBaseId}`}
                                  value={step.label}
                                  onChange={(e) => {
                                    const updatedSteps = productionTimeline.steps.map((s) =>
                                      s.key === step.key ? { ...s, label: e.target.value } : s
                                    );
                                    setProductionTimeline({ ...productionTimeline, steps: updatedSteps });
                                  }}
                                  maxLength={60}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor={`custom-description-${customBaseId}`}>Descrição (opcional)</Label>
                                <Input
                                  id={`custom-description-${customBaseId}`}
                                  value={step.description}
                                  onChange={(e) => {
                                    const updatedSteps = productionTimeline.steps.map((s) =>
                                      s.key === step.key ? { ...s, description: e.target.value } : s
                                    );
                                    setProductionTimeline({ ...productionTimeline, steps: updatedSteps });
                                  }}
                                  maxLength={160}
                                  placeholder="Descrição breve"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Ícone</Label>
                                <div className="flex gap-2 flex-wrap">
                                  {[
                                    { name: "Clock", icon: Clock },
                                    { name: "Play", icon: Play },
                                    { name: "Package", icon: Package },
                                    { name: "CheckCircle", icon: CheckCircle },
                                    { name: "Truck", icon: Truck },
                                    { name: "CheckCircle2", icon: CheckCircle2 },
                                  ].map(({ name, icon: IconComponent }) => (
                                    <button
                                      key={name}
                                      onClick={() => {
                                        const updatedSteps = productionTimeline.steps.map((s) =>
                                          s.key === step.key ? { ...s, icon: name } : s
                                        );
                                        setProductionTimeline({ ...productionTimeline, steps: updatedSteps });
                                      }}
                                      className={`p-2 rounded border-2 transition-all ${
                                        step.icon === name
                                          ? 'border-purple-600 bg-purple-100'
                                          : 'border-gray-200 hover:border-purple-200'
                                      }`}
                                    >
                                      <IconComponent className="w-5 h-5" />
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label>Cor do Ícone</Label>
                                <div className="flex gap-2 flex-wrap">
                                  {[
                                    { name: "blue", label: "Azul", hex: "#3b82f6" },
                                    { name: "red", label: "Vermelho", hex: "#ef4444" },
                                    { name: "green", label: "Verde", hex: "#10b981" },
                                    { name: "yellow", label: "Amarelo", hex: "#eab308" },
                                    { name: "purple", label: "Roxo", hex: "#a855f7" },
                                    { name: "gray", label: "Cinza", hex: "#6b7280" },
                                  ].map(({ name, label, hex }) => (
                                    <button
                                      key={name}
                                      onClick={() => {
                                        const updatedSteps = productionTimeline.steps.map((s) =>
                                          s.key === step.key ? { ...s, color: name } : s
                                        );
                                        setProductionTimeline({ ...productionTimeline, steps: updatedSteps });
                                      }}
                                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                                        step.color === name
                                          ? 'border-gray-800 scale-110'
                                          : 'border-gray-300 hover:border-gray-500'
                                      }`}
                                      style={{ backgroundColor: hex }}
                                      title={label}
                                    />
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </>
                  )}
                </div>
                <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <Button
                    variant="ghost"
                    onClick={handleResetStatuses}
                    disabled={isResettingStatusConfigs}
                  >
                    {isResettingStatusConfigs && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Restaurar todas
                  </Button>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setIsCustomizeDialogOpen(false)}
                      disabled={isSavingStatusConfigs}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="outline"
                      onClick={async () => {
                        setIsSavingStatusConfigs(true);
                        try {
                          if (productionTimeline?.steps) {
                            const result = await applyStepsAsDefault(productionTimeline.steps);
                            if (result.ok) {
                              toast.success("Ordem aplicada como padrão para todos os pedidos!");
                            } else {
                              toast.error(result.error || "Erro ao aplicar como padrão");
                            }
                          }
                        } catch (error) {
                          console.error("Erro:", error);
                          toast.error("Erro ao aplicar como padrão");
                        } finally {
                          setIsSavingStatusConfigs(false);
                        }
                      }}
                      disabled={isSavingStatusConfigs}
                      className="text-purple-600 border-purple-200 hover:bg-purple-50"
                    >
                      {isSavingStatusConfigs && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Aplicar como padrão
                    </Button>
                    <Button onClick={handleSaveStatusDraft} disabled={isSavingStatusConfigs}>
                      {isSavingStatusConfigs && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Salvar alterações
                    </Button>
                  </div>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <CardContent className="pt-4">
              <div className="space-y-5">
                {/* Timeline Mode Switcher - Minimalista */}
                <div className="space-y-2">
                  <div className="flex justify-center pb-2 border-b border-purple-100">
                    <TimelineModeSwitcher
                      selectedMode={selectedMode}
                      onModeChange={setSelectedMode}
                      onCustomModeSelected={handleCustomModeSelected}
                    />
                  </div>

                  {/* Card informativo para modo custom */}
                  {showCustomModeInfo && !isDefaultModeCustom && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 animate-in fade-in slide-in-from-top-2 duration-300">
                      <div className="flex gap-2 items-start">
                        <span className="text-lg flex-shrink-0">💡</span>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-blue-900">Modo Personalizado</p>
                          <p className="text-xs text-blue-700 mt-1">
                            Configure etapas customizadas em{" "}
                            <button
                              onClick={() => navigate('/configuracoes/timeline')}
                              className="underline hover:text-blue-800 font-medium cursor-pointer"
                            >
                              Configurações
                            </button>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* STATUS do Pedido - Apenas etapas padrão (sem customizadas) */}
                {steps.filter(step => !step.key?.startsWith('CUSTOM_')).map((step, index) => {
                  const currentStepIndex = productionTimeline?.current_step_key
                    ? productionTimeline.steps.findIndex(s => s.key === productionTimeline.current_step_key)
                    : currentStep;

                  const isDone = index <= currentStepIndex;
                  const isCurrent = index === currentStepIndex;
                  const IconComponent = step.icon;
                  const statusNotes = statusNotesMap[step.key] || "";

                  return (
                    <div key={step.key} className="flex items-start gap-4">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                        isDone
                          ? 'bg-green-100 border-green-500 text-green-600'
                          : isCurrent
                          ? 'bg-blue-100 border-blue-500 text-blue-600'
                          : 'bg-gray-100 border-gray-300 text-gray-400'
                      }`}>
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium flex items-center gap-2 ${
                          isDone ? 'text-green-700' : isCurrent ? 'text-blue-700' : 'text-gray-500'
                        }`}>
                          {step.label}
                          {productionTimeline?.current_step_key === step.key && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-bold">
                              ATUAL
                            </span>
                          )}
                        </div>
                        <div className={`text-sm flex items-center gap-2 ${
                          isDone ? 'text-green-600' : isCurrent ? 'text-blue-600' : 'text-gray-400'
                        }`}>
                          <span>{step.description}</span>
                          {statusNotes && (
                            <button
                              onClick={() => setExpandedObsKey(expandedObsKey === step.key ? null : step.key)}
                              className="text-gray-600 hover:text-blue-600 transition flex-shrink-0"
                              title="Mostrar observações"
                            >
                              <MessageCircle className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        {isCurrent && (
                          <div className="mt-2">
                            <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border border-blue-200 bg-blue-50 text-blue-700">
                              Status Atual
                            </span>
                          </div>
                        )}
                        {expandedObsKey === step.key && statusNotes && (
                          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="text-xs font-semibold text-blue-900 mb-2">Observações:</p>
                            <p className="text-sm text-blue-800 mb-2">{statusNotes}</p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  setEditingObsKey(step.key);
                                  setEditingObsValue(statusNotes);
                                  setExpandedObsKey(null);
                                }}
                                className="text-xs text-blue-600 hover:underline"
                              >
                                Editar
                              </button>
                              <button
                                onClick={async () => {
                                  const updated = { ...statusNotesMap };
                                  delete updated[step.key];
                                  setStatusNotesMap(updated);
                                  setExpandedObsKey(null);

                                  // Salvar no banco
                                  if (orderDb?.id) {
                                    await saveStatusNotes(orderDb.id, updated);
                                    toast.success("Observação removida!");
                                  }
                                }}
                                className="text-xs text-red-600 hover:underline"
                              >
                                Deletar
                              </button>
                            </div>
                          </div>
                        )}
                        {!statusNotes && (
                          <button
                            onClick={() => {
                              setEditingObsKey(step.key);
                              setEditingObsValue("");
                            }}
                            className="mt-1 text-[10px] text-gray-400 hover:text-gray-600 transition inline-flex items-center gap-0.5"
                          >
                            <MessageCircle className="w-3 h-3" />
                            <span>Adicionar obs</span>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Etapas de Produção Customizadas */}
                {productionTimeline?.steps && productionTimeline.steps.length > 0 && (
                  <>
                    {productionTimeline.steps
                      .filter(step => step.key.startsWith('CUSTOM_'))
                      .map((step) => {
                        const iconMap: { [key: string]: typeof CheckCircle } = {
                          Clock,
                          Play,
                          Package,
                          CheckCircle,
                          Truck,
                          CheckCircle2,
                        };
                        const IconComponent = iconMap[step.icon || "CheckCircle"] || CheckCircle;

                        // Cores para cada opção
                        const colorMap: { [key: string]: { bg: string; border: string; text: string } } = {
                          blue: { bg: "bg-blue-100", border: "border-blue-500", text: "text-blue-600" },
                          red: { bg: "bg-red-100", border: "border-red-500", text: "text-red-600" },
                          green: { bg: "bg-green-100", border: "border-green-500", text: "text-green-600" },
                          yellow: { bg: "bg-yellow-100", border: "border-yellow-500", text: "text-yellow-600" },
                          purple: { bg: "bg-purple-100", border: "border-purple-500", text: "text-purple-600" },
                          gray: { bg: "bg-gray-100", border: "border-gray-500", text: "text-gray-600" },
                        };
                        const stepColor = colorMap[step.color || "blue"] || colorMap.blue;

                        return (
                        <div key={step.key} className="flex items-start gap-4">
                          <button
                            onClick={async () => {
                              if (orderDb?.id) {
                                const result = step.completed
                                  ? await uncompleteProductionStep(orderDb.id, step.key)
                                  : await completeProductionStep(orderDb.id, step.key);

                                if (result.ok) {
                                  const updatedSteps = productionTimeline.steps.map((s) =>
                                    s.key === step.key ? { ...s, completed: !s.completed } : s
                                  );
                                  setProductionTimeline({ ...productionTimeline, steps: updatedSteps });
                                  toast.success(step.completed ? "Etapa marcada como pendente" : "Etapa concluída!");
                                } else {
                                  toast.error(result.error || "Erro ao atualizar etapa");
                                }
                              }
                            }}
                            className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center border-2 cursor-pointer transition hover:scale-110 ${
                              step.completed
                                ? `${stepColor.bg} ${stepColor.border} ${stepColor.text}`
                                : 'bg-gray-100 border-gray-300 text-gray-400 hover:border-gray-400'
                            }`}
                            title={step.completed ? "Marcar como pendente" : "Marcar como concluída"}
                          >
                            <IconComponent className="w-5 h-5" />
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className={`font-medium flex items-center gap-2 ${
                              step.completed ? stepColor.text : 'text-gray-500'
                            }`}>
                              {step.label}
                              {productionTimeline.current_step_key === step.key && (
                                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-bold">
                                  ATUAL
                                </span>
                              )}
                            </div>
                            <div className={`text-sm flex items-center gap-2 ${
                              step.completed ? stepColor.text : 'text-gray-400'
                            }`}>
                              <span>{step.description}</span>
                              {step.notes && (
                                <button
                                  onClick={() => setExpandedObsKey(expandedObsKey === step.key ? null : step.key)}
                                  className="text-gray-600 hover:text-purple-600 transition flex-shrink-0"
                                  title="Mostrar observações"
                                >
                                  <MessageCircle className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                            {expandedObsKey === step.key && step.notes && (
                              <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                                <p className="text-xs font-semibold text-purple-900 mb-2">Observações:</p>
                                <p className="text-sm text-purple-800 mb-2">{step.notes}</p>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      setEditingObsKey(step.key);
                                      setEditingObsValue(step.notes);
                                      setExpandedObsKey(null);
                                    }}
                                    className="text-xs text-purple-600 hover:underline"
                                  >
                                    Editar
                                  </button>
                                  <button
                                    onClick={async () => {
                                      if (orderDb?.id) {
                                        await updateProductionStep(orderDb.id, step.key, { notes: "" });
                                        const updatedSteps = productionTimeline.steps.map((s) =>
                                          s.key === step.key ? { ...s, notes: "" } : s
                                        );
                                        setProductionTimeline({ ...productionTimeline, steps: updatedSteps });
                                        setExpandedObsKey(null);
                                        toast.success("Observação removida!");
                                      }
                                    }}
                                    className="text-xs text-red-600 hover:underline"
                                  >
                                    Deletar
                                  </button>
                                </div>
                              </div>
                            )}
                            {!step.notes && (
                              <button
                                onClick={() => {
                                  setEditingObsKey(step.key);
                                  setEditingObsValue("");
                                }}
                                className="mt-1 text-[10px] text-gray-400 hover:text-gray-600 transition inline-flex items-center gap-0.5"
                              >
                                <MessageCircle className="w-3 h-3" />
                                <span>Adicionar obs</span>
                              </button>
                            )}
                          </div>
                        </div>
                        );
                      })}
                  </>
                )}
              </div>
            </CardContent>

            {/* Dialog para observações ou adicionar etapa */}
            <Dialog open={!!editingObsKey} onOpenChange={(open) => {
              if (!open) {
                setEditingObsKey(null);
                setNewStepLabel("");
                setNewStepDescription("");
                setNewStepIcon("CheckCircle");
                setNewStepColor("blue");
              }
            }}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {editingObsKey === "NEW_STEP" ? "Adicionar Etapa Customizada" : "Adicionar Observações"}
                  </DialogTitle>
                  <DialogDescription>
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  {editingObsKey === "NEW_STEP" ? (
                    <>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                        <strong>💡 Dica:</strong> Etapas adicionadas aqui são <strong>específicas deste pedido</strong>. Para mudar as etapas padrão de todos os pedidos, acesse Configurações → Timeline de Produção.
                      </div>
                      <div>
                        <Label>Nome da Etapa *</Label>
                        <Input
                          placeholder="Ex: Costureira Externa, Revisão Final..."
                          value={newStepLabel}
                          onChange={(e) => setNewStepLabel(e.target.value)}
                          className="mt-1"
                          autoFocus
                        />
                      </div>
                      <div>
                        <Label>Descrição (opcional)</Label>
                        <Input
                          placeholder="Descrição breve"
                          value={newStepDescription}
                          onChange={(e) => setNewStepDescription(e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Ícone</Label>
                        <div className="flex gap-2 mt-2">
                          {[
                            { name: "Clock", icon: Clock },
                            { name: "Play", icon: Play },
                            { name: "Package", icon: Package },
                            { name: "CheckCircle", icon: CheckCircle },
                            { name: "Truck", icon: Truck },
                            { name: "CheckCircle2", icon: CheckCircle2 },
                          ].map(({ name, icon: IconComponent }) => (
                            <button
                              key={name}
                              onClick={() => setNewStepIcon(name)}
                              className={`p-2 rounded border-2 transition-all ${
                                newStepIcon === name
                                  ? 'border-purple-600 bg-purple-50'
                                  : 'border-gray-200 hover:border-purple-200'
                              }`}
                            >
                              <IconComponent className="w-5 h-5" />
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label>Cor do Ícone</Label>
                        <div className="flex gap-2 mt-2">
                          {[
                            { name: "blue", label: "Azul", hex: "#3b82f6" },
                            { name: "red", label: "Vermelho", hex: "#ef4444" },
                            { name: "green", label: "Verde", hex: "#10b981" },
                            { name: "yellow", label: "Amarelo", hex: "#eab308" },
                            { name: "purple", label: "Roxo", hex: "#a855f7" },
                            { name: "gray", label: "Cinza", hex: "#6b7280" },
                          ].map(({ name, label, hex }) => (
                            <button
                              key={name}
                              onClick={() => setNewStepColor(name)}
                              className={`w-10 h-10 rounded-full border-2 transition-all ${
                                newStepColor === name
                                  ? 'border-gray-800 scale-110'
                                  : 'border-gray-300 hover:border-gray-500'
                              }`}
                              style={{ backgroundColor: hex }}
                              title={label}
                            />
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          onClick={() => setEditingObsKey(null)}
                          disabled={isSavingNewStep}
                        >
                          Cancelar
                        </Button>
                        <Button
                          onClick={handleAddNewStep}
                          disabled={isSavingNewStep || !newStepLabel.trim()}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {isSavingNewStep && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          Adicionar
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <Textarea
                        placeholder="Ex: Costureira: Ana Silva - Tel: 11 98765-4321"
                        value={editingObsValue}
                        onChange={(e) => setEditingObsValue(e.target.value)}
                        rows={4}
                      />
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          onClick={() => setEditingObsKey(null)}
                          disabled={isSavingObs}
                        >
                          Cancelar
                        </Button>
                        <Button
                          onClick={() => handleSaveObservation(editingObsKey!)}
                          disabled={isSavingObs}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          {isSavingObs && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          Salvar
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Dialog para editar etapa customizada */}
            <Dialog open={!!editingStepKey} onOpenChange={(open) => {
              if (!open) {
                setEditingStepKey(null);
                setEditingStepLabel("");
                setEditingStepDescription("");
                setEditingStepIcon("CheckCircle");
                setEditingStepColor("blue");
              }
            }}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Editar Etapa Customizada</DialogTitle>
                  <DialogDescription>
                    Edite as informações da etapa personalizada
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Nome da Etapa *</Label>
                    <Input
                      placeholder="Ex: Costureira Externa, Revisão Final..."
                      value={editingStepLabel}
                      onChange={(e) => setEditingStepLabel(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Descrição (opcional)</Label>
                    <Input
                      placeholder="Descrição breve"
                      value={editingStepDescription}
                      onChange={(e) => setEditingStepDescription(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Ícone</Label>
                    <div className="flex gap-2 mt-2">
                      {[
                        { name: "Clock", icon: Clock },
                        { name: "Play", icon: Play },
                        { name: "Package", icon: Package },
                        { name: "CheckCircle", icon: CheckCircle },
                        { name: "Truck", icon: Truck },
                        { name: "CheckCircle2", icon: CheckCircle2 },
                      ].map(({ name, icon: IconComponent }) => (
                        <button
                          key={name}
                          onClick={() => setEditingStepIcon(name)}
                          className={`p-2 rounded border-2 transition-all ${
                            editingStepIcon === name
                              ? 'border-purple-600 bg-purple-50'
                              : 'border-gray-200 hover:border-purple-200'
                          }`}
                        >
                          <IconComponent className="w-5 h-5" />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label>Cor do Ícone</Label>
                    <div className="flex gap-2 mt-2">
                      {[
                        { name: "blue", label: "Azul", hex: "#3b82f6" },
                        { name: "red", label: "Vermelho", hex: "#ef4444" },
                        { name: "green", label: "Verde", hex: "#10b981" },
                        { name: "yellow", label: "Amarelo", hex: "#eab308" },
                        { name: "purple", label: "Roxo", hex: "#a855f7" },
                        { name: "gray", label: "Cinza", hex: "#6b7280" },
                      ].map(({ name, label, hex }) => (
                        <button
                          key={name}
                          onClick={() => setEditingStepColor(name)}
                          className={`w-10 h-10 rounded-full border-2 transition-all ${
                            editingStepColor === name
                              ? 'border-gray-800 scale-110'
                              : 'border-gray-300 hover:border-gray-500'
                          }`}
                          style={{ backgroundColor: hex }}
                          title={label}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      onClick={() => setEditingStepKey(null)}
                      disabled={isSavingEditStep}
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleSaveEditStep}
                      disabled={isSavingEditStep || !editingStepLabel.trim()}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {isSavingEditStep && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Salvar
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {productionTimeline && productionTimeline.steps.length < 15 && (
              <div className="border-t border-gray-200/50 p-4">
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => {
                    console.log("🔴 Clicou em Adicionar Etapa Customizada");
                    setEditingObsKey("NEW_STEP");
                    setEditingObsValue("");
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  + Adicionar Etapa Customizada
                </Button>
              </div>
            )}

            <div className="border-t border-gray-200/50 p-4">
              <Button
                className="w-full"
                size="lg"
                onClick={() => {
                  setIsStatusDialogOpen(true);
                }}
              >
                <Upload className="w-4 h-4 mr-2" />
                Atualizar Status
              </Button>
              <Dialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
                  <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Atualizar Status do Pedido</DialogTitle>
                  </DialogHeader>
                  <p className="text-sm text-gray-500">Selecione a nova etapa para este pedido</p>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="status">Novo Status</Label>
                      <Select value={newStatus} onValueChange={setNewStatus}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o status" />
                        </SelectTrigger>
                        <SelectContent>
                          {timelineStepOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleUpdateStatus} className="flex-1">
                        Atualizar
                      </Button>
                      <Button variant="outline" onClick={() => setIsStatusDialogOpen(false)}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Modal de Recebimento ao Marcar como Entregue */}
              <Dialog open={isDeliveryPaymentOpen} onOpenChange={setIsDeliveryPaymentOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Registrar Recebimento</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="delivery-payment">Valor Recebido (R$)</Label>
                      <Input
                        id="delivery-payment"
                        type="number"
                        step="0.01"
                        min="0"
                        value={deliveryPaymentAmount ?? ''}
                        onChange={(e) => setDeliveryPaymentAmount(e.target.value ? parseFloat(e.target.value) : null)}
                        placeholder={`Total do pedido: R$ ${order?.value.toFixed(2)}`}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        Deixe vazio ou 0 para "Não recebeu pagamento"
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleConfirmDeliveryPayment}
                        className="flex-1"
                        disabled={isRegisteringDeliveryPayment}
                      >
                        {isRegisteringDeliveryPayment ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Registrando...
                          </>
                        ) : (
                          "Marcar como Entregue"
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setIsDeliveryPaymentOpen(false);
                          setDeliveryPaymentAmount(null);
                        }}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </Card>

            {/* 💰 Card de Recebimentos com estilo Ateliê Pro */}
            <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100/50 shadow-lg overflow-hidden">
              <CardHeader className="pb-3 border-b border-purple-200/60 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-purple-200/50 flex-shrink-0">
                      <CreditCard className="w-4 h-4 text-purple-700" />
                    </div>
                    <CardTitle className="text-purple-900 text-base m-0 truncate">
                      Recebimentos
                    </CardTitle>
                  </div>
                  {order.value > order.paid && (
                    <Button
                      onClick={() => setIsRegistrarRecebimentoOpen(true)}
                      size="xs"
                      className="gap-1 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white shadow-md whitespace-nowrap text-xs py-1 px-3 flex-shrink-0"
                    >
                      <CreditCard className="w-3 h-3" />
                      Registrar
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                {order && (() => {
                  // Calcular total com desconto
                  const orderDiscount = (order as any)?.discount ?? 0;
                  const orderDiscountType = (order as any)?.discount_type ?? 'fixed';
                  const discountAmount = orderDiscountType === 'percentage'
                    ? (order.value * orderDiscount / 100)
                    : orderDiscount;
                  const totalComDesconto = Math.max(0, order.value - discountAmount);

                  return (
                    <ListaRecebimentos
                      orderId={order.id}
                      totalPedido={totalComDesconto}
                      paidValue={order.paid}
                      onRecebimentoDeleted={() => setRefreshRecebimentos(r => r + 1)}
                    />
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Notas Fiscais */}
        <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100/50 shadow-lg overflow-hidden">
          <CardHeader className="pb-3 border-b border-purple-200/60">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-green-200/50">
                  <FileText className="w-4 h-4 text-green-700" />
                </div>
                <CardTitle className="text-purple-900 text-base m-0">
                  Notas Fiscais
                </CardTitle>
              </div>
              <div className="flex items-center gap-2">
                {empresa?.tem_nota_fiscal && notasComErroCount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLimparNotasComErro}
                    disabled={limpandoNotasErro || emitindoNota}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    {limpandoNotasErro ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="mr-2 h-4 w-4" />
                    )}
                    Limpar erros ({notasComErroCount})
                  </Button>
                )}
                {!empresa?.tem_nota_fiscal && (
                  <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border border-dashed border-amber-300 bg-amber-50 text-amber-700">
                    Exclusivo do plano Profissional
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (!empresa?.tem_nota_fiscal) {
                      toast.info('Visualize e gerencie suas notas fiscais assinando o plano Profissional (com NF).');
                      navigate('/assinatura');
                      return;
                    }
                    navigate('/notas-fiscais');
                  }}
                  className={!empresa?.tem_nota_fiscal ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50' : ''}
                >
                  Ver Todas
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-4">
              {empresa?.tem_nota_fiscal && notasFiscais.length > 0 ? (
                <div className="space-y-2">
                  {notasFiscais.map((nota) => {
                      const isErro = ['erro_emissao', 'erro_autorizacao', 'erro_validacao', 'denegado'].includes(nota.status);
                      const isProcessando = nota.status === 'processando_autorizacao';
                      const requerAjusteNumero =
                        nota?.dados_retornados?.requires_numero_update ||
                        /539|duplicidade/i.test(nota?.erro_mensagem || '');
                      
                      return (
                        <div 
                          key={nota.id} 
                          className={`flex items-start justify-between p-3 border rounded-lg ${
                            isErro || nota.status === 'erro_autorizacao' || nota.status === 'denegado' ? 'border-red-200 bg-red-50' : 
                            isProcessando ? 'border-yellow-200 bg-yellow-50' : 
                            nota.status === 'cancelado' ? 'border-gray-200 bg-gray-50' :
                            nota.status === 'autorizado' || nota.status === 'autorizada' ? 'border-green-200 bg-green-50' :
                            'border-gray-200 bg-gray-50'
                          }`}
                        >
                          <div className="flex-1">
                            <p className="font-medium">NFe {nota.numero || nota.ref}</p>
                            <p className={`text-sm mt-1 ${
                              isErro ? 'text-red-700' : 
                              isProcessando ? 'text-yellow-700' : 
                              'text-green-700'
                            }`}>
                              Status: {
                                nota.status === 'erro_emissao' || nota.status === 'erro_autorizacao' ? '❌ Erro na Emissão' :
                                nota.status === 'processando_autorizacao' ? '⏳ Processando Autorização' :
                                nota.status === 'autorizado' || nota.status === 'autorizada' ? '✅ Autorizada' :
                                nota.status === 'cancelado' ? '❌ Cancelada' :
                                nota.status === 'denegado' ? '⚠️ Denegada' :
                                nota.status
                              }
                            </p>
                            {nota.erro_mensagem && (
                              <p className="text-xs text-red-600 mt-1 italic">
                                {nota.erro_mensagem.length > 100 
                                  ? nota.erro_mensagem.substring(0, 100) + '...' 
                                  : nota.erro_mensagem}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            {nota.status === 'processando_autorizacao' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleConsultarNota(nota.ref)}
                                disabled={consultandoNota === nota.ref || isHomologacaoSemTokenMemo}
                              >
                                {consultandoNota === nota.ref ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-4 w-4 mr-2" />
                                )}
                                Consultar
                              </Button>
                            )}
                            {(isErro || nota.status === 'erro_autorizacao' || nota.status === 'denegado') && (
                              <div className="flex flex-col gap-2">
                                {requerAjusteNumero && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      navigate('/configuracao-focusnf', {
                                        state: { highlight: 'numeracao' },
                                      })
                                    }
                                  >
                                    Ajustar numeração
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setIsReemitFlow(true);
                                    handleEmitirNota();
                                  }}
                                  disabled={emitindoNota || isHomologacaoSemTokenMemo}
                                >
                                  Reemitir
                                </Button>
                              </div>
                            )}
                            {nota.status === 'autorizado' && (
                              <>
                                {nota.xml_url && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const baseUrl = nota.ambiente === 'producao' 
                                        ? 'https://api.focusnfe.com.br'
                                        : 'https://homologacao.focusnfe.com.br';
                                      window.open(`${baseUrl}${nota.xml_url}`, '_blank');
                                    }}
                                  >
                                    <Download className="h-4 w-4 mr-2" />
                                    XML
                                  </Button>
                                )}
                                {nota.danfe_url && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const baseUrl = nota.ambiente === 'producao' 
                                        ? 'https://api.focusnfe.com.br'
                                        : 'https://homologacao.focusnfe.com.br';
                                      window.open(`${baseUrl}${nota.danfe_url}`, '_blank');
                                    }}
                                  >
                                    <FileText className="h-4 w-4 mr-2" />
                                    DANFE
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => navigate('/notas-fiscais')}
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  Ver Detalhes
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                  })}
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    {empresa?.tem_nota_fiscal
                      ? 'Nenhuma nota fiscal emitida para este pedido'
                      : 'Recurso disponível no plano Profissional (com NF). Atualize seu plano para emitir e acompanhar notas fiscais por aqui.'}
                  </p>
                </div>
              )}
              <Button
                onClick={() => {
                  if (!empresa?.tem_nota_fiscal) {
                    toast.info('Emita notas fiscais assinando o plano Profissional (com NF).');
                    navigate('/assinatura');
                    return;
                  }
                  setIsReemitFlow(hasNotaAutorizada);
                  handleEmitirNota();
                }}
                disabled={emitindoNota || isHomologacaoSemTokenMemo}
                aria-disabled={!empresa?.tem_nota_fiscal}
                className={`w-full ${!empresa?.tem_nota_fiscal ? 'bg-gray-100 text-gray-500 hover:bg-gray-100 cursor-pointer border border-dashed border-gray-300' : ''}`}
              >
                <FileText className="mr-2 h-4 w-4" />
                {empresa?.tem_nota_fiscal
                  ? hasNotaAutorizada
                    ? 'Reemitir Nota Fiscal'
                    : 'Emitir Nota Fiscal'
                  : 'Disponível no plano Profissional'}
              </Button>
              {isHomologacaoSemTokenMemo && (
                <p className="text-xs text-red-600 text-center">
                  Token de homologação não configurado. Cadastre o emitente e preencha o token de homologação.
                </p>
              )}
              {!empresa?.tem_nota_fiscal && (
                <p className="text-xs text-gray-500 text-center">
                  Assine o plano Profissional (com NF) para liberar esta funcionalidade.
                </p>
              )}

              {/* Dialog para configurar itens */}
              {/* ✨ DEBUG: Log dos catalog_products sendo passados */}
              {dialogEmitirNotaOpen && (() => {
                const catalogProducts = (order as any)?.catalog_products;
                console.log('[PedidoDetalhe] Abrindo modal com - DETALHADO:', {
                  personalizations: order?.personalizations,
                  personalizationsLength: order?.personalizations?.length || 0,
                  catalogProducts: catalogProducts,
                  catalogProductsLength: catalogProducts?.length || 0,
                  catalogProductsType: typeof catalogProducts,
                  catalogProductsIsArray: Array.isArray(catalogProducts),
                  orderCode: (order as any)?.code || code,
                });
                console.log('[PedidoDetalhe] 🔍 PRÉ-PASSAGEM - catalog_products DETALHADO:', {
                  value: catalogProducts,
                  type: typeof catalogProducts,
                  isArray: Array.isArray(catalogProducts),
                  isObject: catalogProducts !== null && typeof catalogProducts === 'object',
                  keys: catalogProducts ? Object.keys(catalogProducts) : [],
                  length: catalogProducts?.length || 0,
                  stringified: JSON.stringify(catalogProducts, null, 2)
                });
                return null;
              })()}
              <DialogEmitirNota
                open={dialogEmitirNotaOpen}
                onOpenChange={(open) => {
                  setDialogEmitirNotaOpen(open);
                  if (!open) {
                    setIsReemitFlow(false);
                  }
                }}
                onEmitir={handleConfirmarEmitirNota}
                valorTotalPadrao={order?.value || 0}
                orderCode={(order as any)?.code || code}
                loading={emitindoNota}
                clienteInicial={clienteInicial}
                // 📅 NOVO: Passar items do pedido para pré-preenchimento
                itensPedido={order?.personalizations || []}
                descricaoPedido={order?.description || ''}
                // ✅ NOVO: Passar produtos do catálogo com dados fiscais
                // ⚠️ Converter Object-array para Array se necessário
                itensPedidoComFiscal={(() => {
                  const catalogProducts = (order as any)?.catalog_products;
                  if (!catalogProducts) return [];
                  
                  // Se for array, retorna como está
                  if (Array.isArray(catalogProducts)) {
                    return catalogProducts;
                  }
                  
                  // Se for object com índices numéricos, converter para array
                  if (typeof catalogProducts === 'object') {
                    const keys = Object.keys(catalogProducts);
                    if (keys.every(k => /^\d+$/.test(k))) {
                      // É um object-array, converter
                      return Object.values(catalogProducts);
                    }
                  }
                  
                  // Fallback
                  return [];
                })()}
              />
            </div>
          </CardContent>
        </Card>

        {/* Ações */}
        <Card id="acoes-section" className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100/50 shadow-lg overflow-hidden">
          <CardHeader className="pb-3 border-b border-purple-200/60">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-purple-200/50">
                <Printer className="w-4 h-4 text-purple-700" />
              </div>
              <CardTitle className="text-purple-900 text-base m-0">
                Ações
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <Button
                onClick={() => {
                  const linkCompartilhado = `${window.location.origin}/pedido/compartilhado/${order.code}`;
                  navigator.clipboard.writeText(linkCompartilhado);
                  toast.success("Link copiado! Compartilhe com o cliente 🎉");
                }}
                variant="outline"
                className="w-full sm:flex-1 bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-600 hover:text-white hover:border-purple-600"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Compartilhar com Cliente
              </Button>
              <Button
                onClick={() => navigate(`/pedidos/${order.id}/producao`)}
                variant="outline"
                className="w-full sm:flex-1 bg-purple-50 border-purple-400 text-purple-800 hover:bg-purple-800 hover:text-white hover:border-purple-800"
              >
                <Printer className="w-4 h-4 mr-2" />
                Gerar Ordem de Produção
              </Button>
              <Button
                onClick={generateEmployeeTechnicalSheet}
                variant="outline"
                className="w-full sm:flex-1 bg-purple-50 border-purple-400 text-purple-800 hover:bg-purple-800 hover:text-white hover:border-purple-800"
              >
                <Users className="w-4 h-4 mr-2" />
                Ficha Técnica Funcionários
              </Button>
              <Button
                onClick={() => setIsSelectingCupomVia(true)}
                variant="outline"
                className="w-full sm:flex-1 bg-purple-50 border-purple-400 text-purple-800 hover:bg-purple-800 hover:text-white hover:border-purple-800"
              >
                <FileText className="w-4 h-4 mr-2" />
                Imprimir Cupom
              </Button>
              <Button
                onClick={() => setIsReceiptCustomizeOpen(true)}
                variant="outline"
                className="w-full sm:flex-1 bg-purple-50 border-purple-400 text-purple-800 hover:bg-purple-800 hover:text-white hover:border-purple-800"
              >
                <Download className="w-4 h-4 mr-2" />
                Gerar Recibo de Retirada
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* 💰 Modal de Registrar Recebimento */}
        {order && (() => {
          // Calcular saldo devedor considerando desconto
          const orderDiscount = (order as any)?.discount ?? 0;
          const orderDiscountType = (order as any)?.discount_type ?? 'fixed';
          const discountAmount = orderDiscountType === 'percentage'
            ? (order.value * orderDiscount / 100)
            : orderDiscount;
          const totalComDesconto = Math.max(0, order.value - discountAmount);
          const saldoDevedor = totalComDesconto - order.paid;

          return (
            <DialogRegistrarRecebimento
              isOpen={isRegistrarRecebimentoOpen}
              onClose={() => setIsRegistrarRecebimentoOpen(false)}
              orderId={order.id}
              saldoDevedor={saldoDevedor}
              onRecebimentoCriado={() => {
                setRefreshRecebimentos(r => r + 1);
                refetch();
              }}
            />
          );
        })()}

        {/* 📋 Modal de Customização do Recibo de Retirada */}
        <Dialog open={isReceiptCustomizeOpen} onOpenChange={setIsReceiptCustomizeOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Customizar Recibo de Retirada</DialogTitle>
              <DialogDescription>
                Selecione quais informações deseja incluir no recibo
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* 📦 Seção de Itens */}
              <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="includeItems"
                    checked={receiptOptions.includeItems}
                    onChange={(e) => setReceiptOptions({...receiptOptions, includeItems: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="includeItems" className="cursor-pointer font-semibold">
                    📦 Itens do Pedido
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="includePersonalizations"
                    checked={receiptOptions.includePersonalizations}
                    onChange={(e) => setReceiptOptions({...receiptOptions, includePersonalizations: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="includePersonalizations" className="cursor-pointer font-semibold">
                    ✨ Personalizações
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="includeDescription"
                    checked={receiptOptions.includeDescription}
                    onChange={(e) => setReceiptOptions({...receiptOptions, includeDescription: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="includeDescription" className="cursor-pointer font-semibold">
                    📝 Descrição Completa
                  </Label>
                </div>
              </div>

              {/* 📤 Seção de Entrega Parcial */}
              <div className="border rounded-lg p-4 space-y-3 bg-blue-50 border-blue-200">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="isPartialDelivery"
                    checked={receiptOptions.isPartialDelivery}
                    onChange={(e) => {
                      const isPartial = e.target.checked;
                      if (isPartial && order?.catalog_products) {
                        // Inicializar seleção com todas as quantidades
                        const selected: { [key: string]: number } = {};
                        order.catalog_products.forEach((item: any) => {
                          selected[item.id] = item.quantity || 1;
                        });
                        setReceiptOptions({
                          ...receiptOptions,
                          isPartialDelivery: isPartial,
                          selectedItemsForDelivery: selected,
                        });
                      } else {
                        setReceiptOptions({
                          ...receiptOptions,
                          isPartialDelivery: isPartial,
                          selectedItemsForDelivery: {},
                        });
                      }
                    }}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="isPartialDelivery" className="cursor-pointer font-semibold text-blue-900">
                    📤 Entrega Parcial
                  </Label>
                </div>

                {(() => {
                  // Tentar usar catalog_products, personalizations ou items (fallback)
                  const itemsToShow = order?.catalog_products?.length > 0
                    ? order.catalog_products
                    : order?.personalizations?.length > 0
                    ? order.personalizations
                    : order?.items?.length > 0
                    ? order.items
                    : [];

                  return receiptOptions.isPartialDelivery && itemsToShow.length > 0 ? (
                    <div className="space-y-2 ml-6 bg-white p-3 rounded border border-blue-200">
                      <p className="text-sm font-semibold text-slate-700">Selecione quantidade a entregar:</p>
                      {itemsToShow.map((item: any, index: number) => {
                        const itemKey = item.id || item.order_id || `item-${index}`;
                        const totalQty = item.quantity || 1;
                        const selectedQty = receiptOptions.selectedItemsForDelivery[itemKey] || 0;
                        const isPartial = selectedQty < totalQty;
                        const itemName = item.name || item.person_name || item.description || 'Item';

                        return (
                          <div key={itemKey} className="flex items-center justify-between gap-3 p-2 bg-slate-50 rounded">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-700 truncate">{itemName}</p>
                              <p className="text-xs text-slate-500">Total: {totalQty}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="0"
                                max={totalQty}
                                value={selectedQty}
                                onChange={(e) => {
                                  const newSelected = {...receiptOptions.selectedItemsForDelivery};
                                  newSelected[itemKey] = Math.min(Math.max(0, parseInt(e.target.value) || 0), totalQty);
                                  setReceiptOptions({...receiptOptions, selectedItemsForDelivery: newSelected});
                                }}
                                className="w-12 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                              />
                              <span className={`text-xs font-semibold ${isPartial ? 'text-red-600' : 'text-green-600'}`}>
                                {isPartial ? '⚠️' : '✓'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : null;
                })()}
              </div>

              {/* 📅 Seção de Data de Retirada */}
              <div className="border rounded-lg p-4 space-y-3 bg-green-50 border-green-200">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="includeDeliveryDate"
                    checked={receiptOptions.includeDeliveryDate}
                    onChange={(e) => setReceiptOptions({...receiptOptions, includeDeliveryDate: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="includeDeliveryDate" className="cursor-pointer font-semibold text-green-900">
                    📅 Incluir Data de Retirada
                  </Label>
                </div>

                {receiptOptions.includeDeliveryDate && (
                  <div className="ml-6 bg-white p-3 rounded border border-green-200">
                    <input
                      type="date"
                      value={receiptOptions.deliveryDate}
                      onChange={(e) => setReceiptOptions({...receiptOptions, deliveryDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    />
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsReceiptCustomizeOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  console.log('🔘 [CLIQUE] Botão Gerar Recibo clicado!');
                  setIsReceiptCustomizeOpen(false);
                  handleGeneratePickupReceipt();
                }}
                className="animated-gradient-button-purple"
              >
                Gerar Recibo
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 🧾 Modal de Seleção de Via do Cupom - 2 Etapas */}
        <Dialog open={isSelectingCupomVia} onOpenChange={(open) => {
          setIsSelectingCupomVia(open);
          if (!open) {
            setCupomStep('via');
            setCupomPrinterType('thermica');
            setCupomOptions({
              mostrarAvisoBoas: true,
              mostrarAvisoPromocao: true,
              observacaoCustomizada: '',
            });
          }
        }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg max-w-[95vw]">
            {cupomStep === 'via' && (
              <>
                <DialogHeader>
                  <DialogTitle>Selecionar Via do Cupom</DialogTitle>
                  <DialogDescription>
                    Escolha qual via deseja imprimir (bobina contínua)
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-2 sm:space-y-3">
                  <button
                    onClick={() => {
                      setSelectedCupomVia('cliente');
                      setCupomStep('customizacao');
                    }}
                    className="w-full p-3 sm:p-4 border-2 border-blue-200 rounded-lg hover:bg-blue-50 transition-colors text-left"
                  >
                    <div className="font-semibold text-blue-900 text-sm sm:text-base">👤 Via Cliente</div>
                    <div className="text-xs sm:text-sm text-blue-700 mt-0.5 sm:mt-1">Imprimir apenas a via para o cliente</div>
                  </button>

                  <button
                    onClick={() => {
                      setSelectedCupomVia('estabelecimento');
                      setCupomStep('customizacao');
                    }}
                    className="w-full p-3 sm:p-4 border-2 border-purple-200 rounded-lg hover:bg-purple-50 transition-colors text-left"
                  >
                    <div className="font-semibold text-purple-900 text-sm sm:text-base">🏪 Via Estabelecimento</div>
                    <div className="text-xs sm:text-sm text-purple-700 mt-0.5 sm:mt-1">Imprimir apenas a via para controle interno</div>
                  </button>

                  <button
                    onClick={() => {
                      setSelectedCupomVia('ambas');
                      setCupomStep('customizacao');
                    }}
                    className="w-full p-3 sm:p-4 border-2 border-green-200 rounded-lg hover:bg-green-50 transition-colors text-left"
                  >
                    <div className="font-semibold text-green-900 text-sm sm:text-base">📋 Ambas as Vias</div>
                    <div className="text-xs sm:text-sm text-green-700 mt-0.5 sm:mt-1">Imprimir ambas (separadas por linha tracejada)</div>
                  </button>
                </div>

                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setIsSelectingCupomVia(false)}
                    className="w-full sm:w-auto text-sm"
                  >
                    Cancelar
                  </Button>
                </DialogFooter>
              </>
            )}

            {cupomStep === 'customizacao' && (
              <>
                <DialogHeader>
                  <DialogTitle>Customizar Cupom</DialogTitle>
                  <DialogDescription>
                    Escolha quais informações exibir no cupom
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 sm:space-y-4">
                  <div className="space-y-3 border rounded-lg p-3 sm:p-4 bg-gray-50">
                    <p className="text-xs sm:text-sm font-semibold text-gray-700">Avisos Padrão:</p>

                    <div className="flex items-start gap-2 sm:gap-3">
                      <input
                        type="checkbox"
                        id="aviso-liquidacao"
                        checked={cupomOptions.mostrarAvisoBoas}
                        onChange={(e) => setCupomOptions({...cupomOptions, mostrarAvisoBoas: e.target.checked})}
                        className="rounded border-gray-300 w-4 h-4 cursor-pointer mt-0.5 flex-shrink-0"
                      />
                      <label htmlFor="aviso-liquidacao" className="text-xs sm:text-sm cursor-pointer">
                        "NÃO EFETUAMOS TROCAS EM DIAS DE LIQUIDACAO"
                      </label>
                    </div>

                    <div className="flex items-start gap-2 sm:gap-3">
                      <input
                        type="checkbox"
                        id="aviso-promocao"
                        checked={cupomOptions.mostrarAvisoPromocao}
                        onChange={(e) => setCupomOptions({...cupomOptions, mostrarAvisoPromocao: e.target.checked})}
                        className="rounded border-gray-300 w-4 h-4 cursor-pointer mt-0.5 flex-shrink-0"
                      />
                      <label htmlFor="aviso-promocao" className="text-xs sm:text-sm cursor-pointer">
                        "NÃO EFETUAMOS TROCAS DE PEÇAS NA PROMOCAO"
                      </label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="observacao-cupom" className="text-xs sm:text-sm font-semibold text-gray-700">
                      Observação Customizada (opcional):
                    </label>
                    <textarea
                      id="observacao-cupom"
                      value={cupomOptions.observacaoCustomizada}
                      onChange={(e) => setCupomOptions({...cupomOptions, observacaoCustomizada: e.target.value})}
                      placeholder="Ex: Aproveite nosso desconto especial de 10%"
                      className="w-full p-2 sm:p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      rows={3}
                    />
                    <p className="text-xs text-gray-500">Máximo 200 caracteres</p>
                  </div>

                  <div className="space-y-2 border rounded-lg p-3 sm:p-4 bg-blue-50">
                    <label className="text-xs sm:text-sm font-semibold text-gray-700">
                      🖨️ Tipo de Impressora:
                    </label>
                    <div className="space-y-2">
                      <button
                        onClick={() => setCupomPrinterType('thermica')}
                        className={`w-full p-2 sm:p-3 rounded-lg border-2 transition-colors text-left ${
                          cupomPrinterType === 'thermica'
                            ? 'border-blue-500 bg-blue-100 text-blue-900'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-blue-300'
                        }`}
                      >
                        <div className="font-semibold text-xs sm:text-sm">📄 Impressora Térmica (80mm)</div>
                        <div className="text-xs mt-0.5 sm:mt-1 opacity-80">Formato compacto para bobina - cupom atual</div>
                      </button>

                      <button
                        onClick={() => setCupomPrinterType('a4')}
                        className={`w-full p-2 sm:p-3 rounded-lg border-2 transition-colors text-left ${
                          cupomPrinterType === 'a4'
                            ? 'border-green-500 bg-green-100 text-green-900'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-green-300'
                        }`}
                      >
                        <div className="font-semibold text-xs sm:text-sm">📋 Impressora Normal (A4)</div>
                        <div className="text-xs mt-0.5 sm:mt-1 opacity-80">Formato visual para papel A4 - mais legível</div>
                      </button>
                    </div>
                  </div>
                </div>

                <DialogFooter className="gap-2 flex-col sm:flex-row">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCupomStep('via');
                      setSelectedCupomVia(null);
                      setCupomPrinterType('thermica');
                    }}
                    className="w-full sm:w-auto text-sm"
                  >
                    Voltar
                  </Button>
                  <Button
                    onClick={() => {
                      if (selectedCupomVia) {
                        generateCupomFiscal(selectedCupomVia, cupomOptions, cupomPrinterType);
                        setIsSelectingCupomVia(false);
                        setCupomStep('via');
                        setCupomPrinterType('thermica');
                        setCupomOptions({
                          mostrarAvisoBoas: true,
                          mostrarAvisoPromocao: true,
                          observacaoCustomizada: '',
                        });
                      }
                    }}
                    className="animated-gradient-button-purple w-full sm:w-auto text-sm"
                  >
                    Imprimir Cupom
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* FASE 3: Modal de Agendamento de Prova */}
        <ProvaUnificadoModal
          isOpen={isProvaModalOpen}
          onClose={() => setIsProvaModalOpen(false)}
          onConfirm={handleConfirmProvaAgendamento}
          clienteNome={order?.customer_name}
          pedidoNumero={code}
          provaExistente={provaAgendada}
        />
      </div>
    </div>
  );
}
